import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";

async function loadJsPDF() {
  if (window.jspdf) return window.jspdf.jsPDF;
  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return window.jspdf.jsPDF;
}

function generatePrintPDF(data) {
  const {
    brandTop, clientTop, brandSecond, clientSecond,
    brandScores, clientScores,
    match, matchLabel, matchColor,
    comm, ARCHETYPES,
    clientName, brandOpenText, clientOpenText,
    matchScore
  } = data;

  const bd = ARCHETYPES[brandTop] || {};
  const cd = ARCHETYPES[clientTop] || {};
  const scoreBarW = Math.round((match.score / 5) * 100);
  const barColor = match.score >= 4 ? "#4CAF50" : match.score === 3 ? "#F59E0B" : "#E74C3C";

  const topArchs = Object.entries(brandScores)
    .sort((a,b) => b[1]-a[1]).slice(0,6);
  const topArchsClient = Object.entries(clientScores)
    .sort((a,b) => b[1]-a[1]).slice(0,6);
  const maxB = topArchs[0]?.[1] || 1;
  const maxC = topArchsClient[0]?.[1] || 1;

  const brandBars = topArchs.map(([n,v]) =>
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px;">
      <span style="min-width:100px;font-size:11px;color:${n===brandTop?bd.accent:'#888'}">${n}</span>
      <div style="flex:1;height:4px;background:#2A2A38;border-radius:99px;overflow:hidden;">
        <div style="width:${Math.round((v/maxB)*100)}%;height:100%;background:${n===brandTop?bd.accent:'#3A3A46'};border-radius:99px;"></div>
      </div>
      <span style="font-size:10px;color:#555;min-width:16px;text-align:right">${v}</span>
    </div>`).join('');

  const clientBars = topArchsClient.map(([n,v]) =>
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px;">
      <span style="min-width:100px;font-size:11px;color:${n===clientTop?cd.accent:'#888'}">${n}</span>
      <div style="flex:1;height:4px;background:#2A2A38;border-radius:99px;overflow:hidden;">
        <div style="width:${Math.round((v/maxC)*100)}%;height:100%;background:${n===clientTop?cd.accent:'#3A3A46'};border-radius:99px;"></div>
      </div>
      <span style="font-size:10px;color:#555;min-width:16px;text-align:right">${v}</span>
    </div>`).join('');

  const recs = [
    { label: "Adresowanie bólu klienta", text: comm.painAddress, accent: "#F59E0B" },
    { label: "Strategia CTA", text: comm.cta, accent: "#F59E0B" },
    { label: "Ton komunikacji", text: comm.tone, accent: "#F59E0B" },
    { label: "Budowanie lojalności", text: comm.loyalty, accent: "#7B4FBF" },
    ...(comm.avoid ? [{ label: "Czego unikać", text: comm.avoid, accent: "#E74C3C" }] : []),
  ];

  const recsHtml = recs.map(r =>
    `<div style="border-left:3px solid ${r.accent};background:#1C1C24;border-radius:0 8px 8px 0;padding:12px 14px;margin-bottom:10px;">
      <div style="font-size:8px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${r.accent};margin-bottom:5px;">${r.label}</div>
      <div style="font-size:12px;color:#C0BEB8;line-height:1.6;">${r.text}</div>
    </div>`).join('');

  const openCtx = (brandOpenText || clientOpenText) ? `
    <div style="background:#16161A;border:1px solid #2A2A38;border-radius:10px;padding:20px;margin-bottom:16px;">
      <div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#555;margin-bottom:12px;">Kontekst otwarty — do weryfikacji na sesji</div>
      ${brandOpenText ? `<div style="margin-bottom:10px;"><div style="font-size:10px;color:#888;margin-bottom:3px;">Opis marki (własny)</div><div style="font-size:12px;color:#C0BEB8;font-style:italic;">"${brandOpenText}"</div></div>` : ''}
      ${clientOpenText ? `<div><div style="font-size:10px;color:#888;margin-bottom:3px;">Opis klienta idealnego (własny)</div><div style="font-size:12px;color:#C0BEB8;font-style:italic;">"${clientOpenText}"</div></div>` : ''}
    </div>` : '';

  // ─────────────────────────────────────────────────────────────────────────────
  // SEKCJA: HIPOTEZA ALGORYTMU vs. OBSERWACJA Z WYWIADU
  // Sercem metodologii Selvus jest weryfikacja wyniku quizu z rzeczywistością
  // ujawnioną w wywiadzie strategicznym. Algorytm mierzy obraz aspiracyjny;
  // wywiad — obraz operacyjny. Trzy scenariusze interpretacji rozjazdu.
  // ─────────────────────────────────────────────────────────────────────────────
  const interviewSection = `
    <div style="background:#16161A;border:1px solid #2A2A38;border-radius:10px;padding:24px;margin-bottom:16px;">
      <div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#F59E0B;margin-bottom:8px;">⚠ Krok następny — weryfikacja na sesji strategicznej</div>
      <div style="font-family:Georgia,serif;font-size:16px;color:#F0EEE8;margin-bottom:14px;">Hipoteza algorytmu vs. obserwacja z wywiadu</div>
      <div style="font-size:12px;color:#888;line-height:1.6;margin-bottom:18px;">
        Wynik tego raportu to <strong style="color:#F0EEE8;">hipoteza oparta na deklaracji</strong> —
        właściciel marki wybrał odpowiedzi, które odzwierciedlają jego aspiracyjny obraz marki i klienta.
        Karta wywiadu strategicznego ujawnia <strong style="color:#F0EEE8;">obraz operacyjny</strong> —
        to, co wynika z cytatów, opisu konkretnych klientów, marży i sposobu działania w praktyce.
        <br><br>
        Te dwa obrazy mogą się rozjeżdżać — i to jest najcenniejsza informacja diagnostyczna.
      </div>

      <!-- SCENARIUSZ 1: SPÓJNOŚĆ -->
      <div style="background:#1A2820;border-left:3px solid #4CAF50;border-radius:6px;padding:14px 16px;margin-bottom:10px;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px;">
          <div style="width:22px;height:22px;border-radius:50%;background:#4CAF50;color:#000;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;">✓</div>
          <div style="font-size:13px;font-weight:700;color:#4CAF50;">Scenariusz 1 — Spójność</div>
        </div>
        <div style="font-size:11px;color:#A8B8AA;line-height:1.6;padding-left:32px;">
          Algorytm i wywiad pokazują ten sam archetyp lub bliskie warianty.
          <strong style="color:#D8E8DA;">Wysoka pewność diagnozy</strong> — projektowanie marki może iść pełną parą.
          Klient zna siebie i swoich klientów. To rzadszy scenariusz niż się wydaje.
        </div>
      </div>

      <!-- SCENARIUSZ 2: ROZJAZD ASPIRACYJNY -->
      <div style="background:#2A1F0F;border-left:3px solid #F59E0B;border-radius:6px;padding:14px 16px;margin-bottom:10px;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px;">
          <div style="width:22px;height:22px;border-radius:50%;background:#F59E0B;color:#000;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;">!</div>
          <div style="font-size:13px;font-weight:700;color:#F59E0B;">Scenariusz 2 — Rozjazd aspiracyjny (marka)</div>
        </div>
        <div style="font-size:11px;color:#C0B090;line-height:1.6;padding-left:32px;">
          Algorytm pokazuje archetyp marki <strong style="color:#E8D8B0;">X</strong>, ale wywiad ujawnia archetyp <strong style="color:#E8D8B0;">Y</strong>
          — w cytatach, języku codziennej obsługi klienta, sposobie opisywania pracy.
          <strong style="color:#E8D8B0;">Klient projektuje markę przeciw sobie.</strong>
          Strona w tonie aspiracyjnym odepchnie tych klientów, których obecnie obsługuje najlepiej.
          Decyzja strategiczna: pójść za aspiracją (i przebudować ofertę) lub za rzeczywistością (i wzmocnić to, co działa).
        </div>
      </div>

      <!-- SCENARIUSZ 3: ROZJAZD EKSPOZYCYJNY -->
      <div style="background:#2A1A1F;border-left:3px solid #C0392B;border-radius:6px;padding:14px 16px;margin-bottom:0;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px;">
          <div style="width:22px;height:22px;border-radius:50%;background:#C0392B;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;">⚡</div>
          <div style="font-size:13px;font-weight:700;color:#C0392B;">Scenariusz 3 — Rozjazd ekspozycyjny (klient)</div>
        </div>
        <div style="font-size:11px;color:#C09090;line-height:1.6;padding-left:32px;">
          Algorytm pokazuje archetyp klienta idealnego, ale w wywiadzie wymienieni „najlepsi klienci ostatniego roku" pasują do innego archetypu.
          <strong style="color:#E8B8B8;">Klient sprzedaje innym, niż obsługuje.</strong>
          Strona zaprojektowana pod „idealnego klienta z głowy" przyciągnie ruch, który nie konwertuje.
          Decyzja: skalibrować idealnego klienta na bazie realnych danych — marży, lojalności, źródła poleceń.
        </div>
      </div>

      <div style="margin-top:18px;padding-top:14px;border-top:1px solid #2A2A38;font-size:10px;color:#666;line-height:1.6;font-style:italic;">
        Karta wywiadu strategicznego (sekcje C–E) jest zaprojektowana właśnie po to, żeby ten rozjazd ujawnić.
        Wynik diagnozy to nie pojedynczy archetyp — to <strong style="color:#888;">mapa relacji między tym, kim klient chce być, a tym, kim jest w oczach swoich klientów.</strong>
      </div>
    </div>`;

  const dateStr = new Date().toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" });

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
<meta charset="UTF-8">
<title>Raport Archetypowy — ${clientName || 'Selvus™'}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0B0B0D; color: #F0EEE8; font-family: 'Segoe UI', Arial, sans-serif; padding: 32px; max-width: 800px; margin: 0 auto; }
  @media print {
    body { padding: 0; max-width: 100%; }
    .no-print { display: none !important; }
    @page { margin: 15mm; size: A4; }
  }
  h1 { font-family: Georgia, serif; }
</style>
</head>
<body>

<!-- PRINT BUTTON -->
<div class="no-print" style="text-align:right;margin-bottom:20px;">
  <button onclick="window.print()" style="background:#F59E0B;color:#000;border:none;padding:10px 22px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;">
    🖨 Drukuj / Zapisz jako PDF
  </button>
</div>

<!-- HEADER -->
<div style="background:linear-gradient(135deg,#1C1C2E,#0E0E12);border-radius:12px;padding:28px 32px;margin-bottom:16px;border:1px solid #2A2A38;">
  <div style="font-size:9px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:#F59E0B;margin-bottom:10px;">SELVUS™ · DIAGNOZA ARCHETYPÓW</div>
  <h1 style="font-size:26px;color:#F0EEE8;margin-bottom:8px;">Raport Archetypowy</h1>
  <div style="font-size:12px;color:#888;">${dateStr}${clientName ? ` · Klient: <strong style="color:#F59E0B;">${clientName}</strong>` : ''}</div>
</div>

<!-- ARCH CARDS -->
<div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;">
  <div style="background:#16161A;border:1px solid #2A2A38;border-top:3px solid ${bd.accent || '#F59E0B'};border-radius:8px;padding:20px;">
    <div style="font-size:8px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:${bd.accent || '#F59E0B'};margin-bottom:10px;">ARCHETYP MARKI</div>
    <div style="font-size:22px;margin-bottom:6px;">${bd.emoji || '◈'}</div>
    <div style="font-family:Georgia,serif;font-size:20px;font-weight:700;color:#F0EEE8;margin-bottom:4px;">${brandTop}</div>
    <div style="font-size:12px;color:${bd.accent || '#F59E0B'};font-style:italic;margin-bottom:12px;">"${bd.tagline || ''}"</div>
    <div style="font-size:11px;color:#888;margin-bottom:4px;"><strong style="color:#aaa;">Strach:</strong> ${bd.fear || ''}</div>
    <div style="font-size:11px;color:#888;"><strong style="color:#aaa;">Lojalność przez:</strong> ${bd.loyalty || ''}</div>
  </div>
  <div style="background:#16161A;border:1px solid #2A2A38;border-top:3px solid ${cd.accent || '#7B4FBF'};border-radius:8px;padding:20px;">
    <div style="font-size:8px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:${cd.accent || '#7B4FBF'};margin-bottom:10px;">ARCHETYP KLIENTA IDEALNEGO</div>
    <div style="font-size:22px;margin-bottom:6px;">${cd.emoji || '◉'}</div>
    <div style="font-family:Georgia,serif;font-size:20px;font-weight:700;color:#F0EEE8;margin-bottom:4px;">${clientTop}</div>
    <div style="font-size:12px;color:${cd.accent || '#7B4FBF'};font-style:italic;margin-bottom:12px;">"${cd.tagline || ''}"</div>
    <div style="font-size:11px;color:#888;margin-bottom:4px;"><strong style="color:#aaa;">Strach:</strong> ${cd.fear || ''}</div>
    <div style="font-size:11px;color:#888;"><strong style="color:#aaa;">Lojalność przez:</strong> ${cd.loyalty || ''}</div>
  </div>
</div>

<!-- SECONDARY NOTE -->
<div style="background:#16161A;border:1px solid #2A2A38;border-radius:8px;padding:12px 16px;margin-bottom:14px;font-size:12px;color:#888;">
  <strong style="color:#F0EEE8;">Archetypy drugiego planu:</strong>
  Marka — <strong style="color:${ARCHETYPES[brandSecond]?.accent || '#aaa'}">${brandSecond}</strong> (${brandScores[brandSecond]} pkt) &nbsp;|&nbsp;
  Klient — <strong style="color:${ARCHETYPES[clientSecond]?.accent || '#aaa'}">${clientSecond}</strong> (${clientScores[clientSecond]} pkt)
</div>

<!-- SCORE BARS -->
<div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;">
  <div style="background:#16161A;border:1px solid #2A2A38;border-radius:8px;padding:18px;">
    <div style="font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#555;margin-bottom:12px;">Rozkład — Marka</div>
    ${brandBars}
  </div>
  <div style="background:#16161A;border:1px solid #2A2A38;border-radius:8px;padding:18px;">
    <div style="font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#555;margin-bottom:12px;">Rozkład — Klient</div>
    ${clientBars}
  </div>
</div>

<!-- MATCH -->
<div style="background:#16161A;border:1px solid #2A2A38;border-radius:10px;padding:20px 24px;margin-bottom:14px;">
  <div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#555;margin-bottom:6px;">SIŁA REZONANSU</div>
  <div style="font-size:13px;color:#888;margin-bottom:12px;">${brandTop} ↔ ${clientTop}</div>
  <div style="display:flex;align-items:center;gap:14px;margin-bottom:10px;">
    <div style="flex:1;height:8px;background:#2A2A38;border-radius:99px;overflow:hidden;">
      <div style="width:${scoreBarW}%;height:100%;background:${barColor};border-radius:99px;"></div>
    </div>
    <div style="font-family:Georgia,serif;font-size:22px;font-weight:700;color:${barColor};min-width:40px;text-align:right;">${match.score}/5</div>
  </div>
  <div style="font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${barColor};margin-bottom:10px;">${matchLabel}</div>
  <div style="font-size:13px;color:#888;line-height:1.6;">${match.desc}</div>
</div>

<!-- DESIGN SIGNALS -->
<div style="background:#16161A;border:1px solid #2A2A38;border-radius:10px;padding:20px 24px;margin-bottom:14px;">
  <div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#555;margin-bottom:16px;">SYGNAŁY PROJEKTOWE</div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
    <div>
      <div style="font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${bd.accent};margin-bottom:10px;">Marka — ${brandTop}</div>
      <div style="font-size:12px;color:#888;line-height:1.8;">
        🎨 Kolor: <span style="color:${bd.accent};font-weight:600;">${bd.accent}</span><br>
        ✦ "${bd.tagline}"<br>
        ❤ Lojalność: ${bd.loyalty}
      </div>
    </div>
    <div>
      <div style="font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${cd.accent};margin-bottom:10px;">Klient — ${clientTop}</div>
      <div style="font-size:12px;color:#888;line-height:1.8;">
        ⚡ Strach: ${cd.fear}<br>
        ✦ "${cd.tagline}"<br>
        ❤ Lojalność: ${cd.loyalty}
      </div>
    </div>
  </div>
</div>

<!-- RECS -->
<div style="background:#16161A;border:1px solid #2A2A38;border-radius:10px;padding:20px 24px;margin-bottom:14px;">
  <div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#555;margin-bottom:16px;">REKOMENDACJE KOMUNIKACYJNE</div>
  ${recsHtml}
</div>

<!-- OPEN CONTEXT -->
${openCtx}

<!-- INTERVIEW HYPOTHESIS vs OBSERVATION -->
${interviewSection}

<!-- CTA -->
<div style="background:linear-gradient(135deg,#1C1C2E,#0E0E12);border:1px solid #F59E0B;border-radius:10px;padding:22px 24px;text-align:center;">
  <div style="font-family:Georgia,serif;font-size:16px;font-weight:700;color:#F0EEE8;margin-bottom:8px;">Ten raport to punkt wyjścia, nie mapa drogowa.</div>
  <div style="font-size:12px;color:#888;line-height:1.6;margin-bottom:16px;">Umów sesję strategiczną — przełożymy wyniki na konkretne decyzje projektowe i komunikacyjne.</div>
  <div style="font-size:13px;font-weight:700;color:#F59E0B;">michalchojnackipro.com</div>
</div>

</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

const EMAILJS_SERVICE_ID = "service_v34pxsw";
const EMAILJS_TEMPLATE_ID = "template_8jzrkue"; // szablon stratega — pełny raport
const EMAILJS_PUBLIC_KEY = "j3LJJk57QX8vtTmRW";

// ─────────────────────────────────────────────────────────────────────────────
// DANE ARCHETYPÓW
// ─────────────────────────────────────────────────────────────────────────────
const ARCHETYPES = {
  Władca:           { color: "#1C1C2E", accent: "#C9A227", light: "#F5F0E0", emoji: "♔", tagline: "Standard bez kompromisu",       fear: "Chaos i utrata kontroli",       loyalty: "Status i potwierdzenie pozycji",  keywords: ["standard", "kontrola", "doskonałość", "prestiż", "pozycja"] },
  Opiekun:          { color: "#2C5F4A", accent: "#7BC67E", light: "#EBF5EE", emoji: "🤲", tagline: "Bezpieczeństwo i troska",       fear: "Krzywda bliskich",              loyalty: "Relacja i zaufanie",             keywords: ["troska", "bezpieczeństwo", "relacja", "wsparcie", "zaufanie"] },
  Mędrzec:          { color: "#1A3A5C", accent: "#5BA4CF", light: "#E8EEF4", emoji: "🔭", tagline: "Prawda i pewność",              fear: "Ignorancja i błędna decyzja",   loyalty: "Wiedza i kompetencja",          keywords: ["wiedza", "analiza", "dowód", "ekspertyza", "precyzja"] },
  Bohater:          { color: "#1A1A1A", accent: "#C0392B", light: "#FDECEA", emoji: "⚔",  tagline: "Zwycięstwo nad przeszkodą",   fear: "Przeciętność i porażka",        loyalty: "Wyniki i dowody skuteczności",  keywords: ["wyniki", "wyzwanie", "mistrzostwo", "przełom", "działanie"] },
  Buntownik:        { color: "#1A1A1A", accent: "#8B0000", light: "#F0EDEA", emoji: "✊", tagline: "Rewolucja systemu",             fear: "Konformizm i uniformizacja",    loyalty: "Wspólne wartości i tożsamość",  keywords: ["zmiana", "opór", "autentyczność", "niezależność", "rewolucja"] },
  Odkrywca:         { color: "#2D5016", accent: "#4A7C2F", light: "#EBF3E4", emoji: "🧭", tagline: "Horyzont możliwości",           fear: "Stagnacja i rutyna",            loyalty: "Nowe doświadczenia",            keywords: ["wolność", "przygoda", "odkrycie", "eksploracja", "autentyczność"] },
  Kochanek:         { color: "#7B1F3A", accent: "#C4596F", light: "#F9EEF2", emoji: "♥",  tagline: "Intensywność odczucia",       fear: "Bycie niekochanym",             loyalty: "Estetyka i doznanie",           keywords: ["piękno", "intensywność", "estetyka", "pożądanie", "bliskość"] },
  Błazen:           { color: "#A85C08", accent: "#F5C842", light: "#FEF5E7", emoji: "★",  tagline: "Radość i lekkość",            fear: "Nuda i przymus powagi",         loyalty: "Autentyczność i humor",         keywords: ["radość", "humor", "lekkość", "zabawa", "spontaniczność"] },
  Czarodziej:       { color: "#2E1760", accent: "#7B4FBF", light: "#F0ECF9", emoji: "✦",  tagline: "Transformacja możliwa",       fear: "Uwięzienie w starym stanie",    loyalty: "Głęboka przemiana",             keywords: ["transformacja", "wizja", "możliwość", "przełom", "magia"] },
  "Zw. Człowiek":   { color: "#4A4A4A", accent: "#7A6A58", light: "#F2F2F0", emoji: "◉",  tagline: "Przynależność bez hierarchii",fear: "Wykluczenie ze wspólnoty",      loyalty: "Autentyczność i dostępność",   keywords: ["równość", "wspólnota", "prostota", "szczerość", "bliskość"] },
  Niewinny:         { color: "#7A6A30", accent: "#D4A820", light: "#FEF9E7", emoji: "☀",  tagline: "Prostota i bezpieczeństwo",   fear: "Świat jest za trudny",          loyalty: "Emocje i nadzieja",             keywords: ["prostota", "nadzieja", "radość", "bezpieczeństwo", "wiara"] },
  Stwórca:          { color: "#5C2A0E", accent: "#B06040", light: "#FAF3EB", emoji: "◈",  tagline: "Kreacja i ekspresja",          fear: "Mediokracja i brak wyrazu",     loyalty: "Jakość narzędzia i procesu",   keywords: ["kreacja", "rzemiosło", "wizja", "jakość", "ekspresja"] },
};

const ARCH_KEYS = Object.keys(ARCHETYPES);

// ─────────────────────────────────────────────────────────────────────────────
// PYTANIA — MODUŁ A: ARCHETYP MARKI (12 pytań, pełna redystrybucja)
// ─────────────────────────────────────────────────────────────────────────────
const BRAND_QUESTIONS = [
  {
    id: "b1",
    text: "Gdy Twoja marka mówi do klienta, jej podstawowy komunikat brzmi:",
    options: [
      { label: "Zapewniamy najwyższy standard — bez kompromisów", scores: { Władca: 3 } },
      { label: "Jesteśmy tu dla Ciebie, cokolwiek się wydarzy", scores: { Opiekun: 3 } },
      { label: "Dajemy Ci wiedzę, by podjąć najlepszą decyzję", scores: { Mędrzec: 3 } },
      { label: "Masz narzędzia, by wygrać — reszta zależy od Ciebie", scores: { Bohater: 3 } },
    ],
  },
  {
    id: "b2",
    text: "Twoja marka istnieje, bo na rynku brakowało:",
    options: [
      { label: "Kogoś, kto naprawdę ustanawia i utrzymuje standardy", scores: { Władca: 2, Mędrzec: 1 } },
      { label: "Narzędzi, które realnie zmieniają możliwości twórcze", scores: { Stwórca: 3 } },
      { label: "Marki, która mówi prawdę bez owijania w bawełnę", scores: { Buntownik: 3 } },
      { label: "Przestrzeni, gdzie każdy czuje się jak swój wśród swoich", scores: { "Zw. Człowiek": 3 } },
    ],
  },
  {
    id: "b3",
    text: "Gdyby Twoja marka była człowiekiem na przyjęciu, byłaby:",
    options: [
      { label: "Osobą, przy której wszyscy czują się bezpiecznie i zaopiekowani", scores: { Opiekun: 3 } },
      { label: "Najbardziej kompetentną osobą w pokoju — cicho, ale pewnie", scores: { Mędrzec: 3 } },
      { label: "Osobą, która rozbawia całe towarzystwo i rozładowuje napięcie", scores: { Błazen: 3 } },
      { label: "Osobą, która mówi rzeczy, których inni boją się powiedzieć", scores: { Buntownik: 3 } },
    ],
  },
  {
    id: "b4",
    text: "Twoja marka najbardziej dumna jest z tego, że:",
    options: [
      { label: "Klienci wracają latami — budujemy relacje, nie transakcje", scores: { Opiekun: 3, "Zw. Człowiek": 1 } },
      { label: "Wyniki naszych klientów są mierzalne i powtarzalne", scores: { Bohater: 3, Mędrzec: 1 } },
      { label: "Zmieniamy to, jak klienci myślą o całej kategorii", scores: { Czarodziej: 3, Buntownik: 1 } },
      { label: "Każde dzieło, które tworzymy, jest niepodrabialne", scores: { Stwórca: 3 } },
    ],
  },
  {
    id: "b5",
    text: "Kiedy marka musi wybrać między ekspansją a jakością, instynktownie wybiera:",
    options: [
      { label: "Jakość zawsze — wolę mniej klientów i wyższy standard", scores: { Władca: 3, Stwórca: 1 } },
      { label: "Głębię relacji — wolę 10 klientów, których naprawdę zmieniam", scores: { Czarodziej: 3, Opiekun: 1 } },
      { label: "Innowację — ekspanduję, ale w nowych kierunkach", scores: { Odkrywca: 3, Stwórca: 1 } },
      { label: "Ekspansję — żeby prostota i dobro były dostępne dla wszystkich", scores: { Niewinny: 3, "Zw. Człowiek": 1 } },
    ],
  },
  {
    id: "b6",
    text: "Największy strach Twojej marki (coś, czego absolutnie chce uniknąć) to:",
    options: [
      { label: "Bycie postrzeganą jako nudna, przewidywalna, bez charakteru", scores: { Błazen: 3, Odkrywca: 1 } },
      { label: "Bycie postrzeganą jako kolejna korporacja bez duszy i humoru", scores: { Buntownik: 3, Błazen: 1 } },
      { label: "Bycie postrzeganą jako marka bez realnych, mierzalnych wyników", scores: { Bohater: 3, Mędrzec: 1 } },
      { label: "Bycie postrzeganą jako marka, która tworzy rzeczy przeciętne", scores: { Stwórca: 3, Władca: 1 } },
    ],
  },
  {
    id: "b7",
    text: "Jakie słowo najlepiej opisuje ton komunikacji Twojej marki?",
    options: [
      { label: "Autorytatywny — mówimy z pozycji eksperta i standardu", scores: { Władca: 2, Mędrzec: 2 } },
      { label: "Ciepły i prosty — mówimy szczerze, bez korporacyjnego żargonu", scores: { Niewinny: 3, "Zw. Człowiek": 1 } },
      { label: "Poetycki i wizjonerski — widzimy więcej niż inni", scores: { Czarodziej: 2, Odkrywca: 2 } },
      { label: "Zmysłowy i estetyczny — piękno jest wartością samą w sobie", scores: { Kochanek: 3 } },
    ],
  },
  {
    id: "b8",
    text: "Twoja marka najlepiej radzi sobie, gdy klient przychodzi z problemem:",
    options: [
      { label: "Nie wiem co kupić / jak wybrać — potrzebuję eksperta", scores: { Mędrzec: 3 } },
      { label: "Jestem zmęczony walką — potrzebuję kogoś, kto mnie odciąży", scores: { Opiekun: 3 } },
      { label: "Chcę czegoś więcej niż to, co oferuje rynek", scores: { Odkrywca: 2, Buntownik: 2 } },
      { label: "Chcę przeżyć coś wyjątkowego — nie kolejnego produktu", scores: { Kochanek: 3 } },
    ],
  },
  {
    id: "b9",
    text: "Największa wartość, jaką Twoja marka wnosi do życia klienta, to:",
    options: [
      { label: "Poczucie kontroli i przewidywalności", scores: { Władca: 3, Mędrzec: 1 } },
      { label: "Poczucie bezpieczeństwa i czystej radości", scores: { Niewinny: 3, Opiekun: 1 } },
      { label: "Intensywne przeżycie i piękno", scores: { Kochanek: 3, Odkrywca: 1 } },
      { label: "Radość, lekkość i śmiech", scores: { Błazen: 3, Niewinny: 1 } },
    ],
  },
  {
    id: "b10",
    text: "Klient, który odchodzi od Twojej konkurencji i przychodzi do Ciebie, najczęściej mówi:",
    options: [
      { label: "Tamci nie dotrzymywali standardów i obietnic", scores: { Władca: 3 } },
      { label: "Tamci traktowali mnie jak numer, a nie jak człowieka i sąsiada", scores: { "Zw. Człowiek": 3, Opiekun: 1 } },
      { label: "Tamci mieli słabe wyniki i nie pokazywali danych", scores: { Bohater: 2, Mędrzec: 2 } },
      { label: "Tamci byli nudni, przewidywalni i bez pomysłu", scores: { Błazen: 2, Odkrywca: 2 } },
    ],
  },
  {
    id: "b11",
    text: "Twoja marka, gdyby miała manifest, zaczynałaby go słowami:",
    options: [
      { label: "Wierzymy, że każdy zasługuje na prostotę i spokój", scores: { Niewinny: 3, Opiekun: 1 } },
      { label: "Wierzymy, że granice istnieją po to, by je przekraczać", scores: { Odkrywca: 3, Bohater: 1 } },
      { label: "Wierzymy, że piękno zmienia sposób, w jaki doświadczamy życia", scores: { Kochanek: 3, Stwórca: 1 } },
      { label: "Wierzymy, że głęboka zmiana jest zawsze możliwa", scores: { Czarodziej: 3 } },
    ],
  },
  {
    id: "b12",
    text: "Twoja marka w relacji z klientem zachowuje się jak:",
    options: [
      { label: "Mentor — prowadzi, uczy, daje mapę", scores: { Mędrzec: 3 } },
      { label: "Partner — jest obok, nie nad", scores: { "Zw. Człowiek": 3, Opiekun: 1 } },
      { label: "Katalizator — wyzwala potencjał który już jest", scores: { Czarodziej: 2, Bohater: 2 } },
      { label: "Rzemieślnik — dostarcza dzieło najwyższej jakości", scores: { Stwórca: 3, Władca: 1 } },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// PYTANIA — MODUŁ B: ARCHETYP KLIENTA IDEALNEGO (12 pytań)
// ─────────────────────────────────────────────────────────────────────────────
const CLIENT_QUESTIONS = [
  {
    id: "c1",
    text: "Twój idealny klient podejmuje decyzję zakupową przede wszystkim gdy:",
    options: [
      { label: "Zobaczył twarde dane, referencje i dowody jakości", scores: { Mędrzec: 3, Władca: 1 } },
      { label: "Poczuł, że mu ufasz i dbasz o jego interes", scores: { Opiekun: 2, "Zw. Człowiek": 2 } },
      { label: "Zobaczył, że inni osiągnęli konkretne wyniki", scores: { Bohater: 3 } },
      { label: "Poczuł emocjonalne połączenie z marką, jej estetyką i prostotą", scores: { Kochanek: 3, Niewinny: 2 } },
    ],
  },
  {
    id: "c2",
    text: "Twój idealny klient NIE kupi, jeśli poczuje że:",
    options: [
      { label: "Produkt/usługa nie dorównuje standardowi, który ma w głowie", scores: { Władca: 3, Stwórca: 1 } },
      { label: "Jest traktowany jak jeden z wielu — bez indywidualnego podejścia i osobowości", scores: { Kochanek: 2, Opiekun: 1, Błazen: 1 } },
      { label: "Marka udaje coś, czym nie jest — wyczuwa fałsz", scores: { Buntownik: 3, "Zw. Człowiek": 1 } },
      { label: "Oferta nie otwiera żadnych nowych drzwi ani fundamentalnej zmiany", scores: { Czarodziej: 3, Odkrywca: 1 } },
    ],
  },
  {
    id: "c3",
    text: "Co napędza Twojego idealnego klienta w pracy i życiu prywatnym?",
    options: [
      { label: "Bycie najlepszym w tym, co robi — wyniki i mistrzostwo", scores: { Bohater: 3 } },
      { label: "Życie z lekkością i humorem — bez nadmiernej powagi i sztuczności", scores: { Błazen: 3, "Zw. Człowiek": 1 } },
      { label: "Tworzenie czegoś własnego — wyrażanie siebie przez pracę", scores: { Stwórca: 3 } },
      { label: "Odkrywanie nowych możliwości i doświadczeń", scores: { Odkrywca: 3 } },
    ],
  },
  {
    id: "c4",
    text: "Twój idealny klient gdy jest niezadowolony z usługi:",
    options: [
      { label: "Oczekuje konkretnych działań naprawczych i dotrzymania słowa", scores: { Władca: 3 } },
      { label: "Chce być wysłuchany i poczuć, że ktoś naprawdę rozumie problem", scores: { Opiekun: 3 } },
      { label: "Szuka racjonalnego wyjaśnienia, co poszło nie tak i dlaczego", scores: { Mędrzec: 3 } },
      { label: "Idzie dalej bez dramatu — ale więcej nie wraca", scores: { "Zw. Człowiek": 2, Odkrywca: 1, Niewinny: 1 } },
    ],
  },
  {
    id: "c5",
    text: "Jak Twój idealny klient podchodzi do ceny Twoich usług?",
    options: [
      { label: "Analizuje value for money — liczy się ROI i dowody jakości", scores: { Mędrzec: 2, Bohater: 2 } },
      { label: "Cena jest drugorzędna — liczy się poczucie wyjątkowości i prestiżu", scores: { Władca: 2, Kochanek: 2 } },
      { label: "Szuka uczciwej, prostej ceny bez ukrytych kosztów i kruczków", scores: { Niewinny: 3, "Zw. Człowiek": 1 } },
      { label: "Inwestuje dużo, gdy wierzy, że to zmieni coś fundamentalnie", scores: { Czarodziej: 3, Stwórca: 1 } },
    ],
  },
  {
    id: "c6",
    text: "Największy ból emocjonalny Twojego idealnego klienta przed zakupem to:",
    options: [
      { label: "Strach przed złą decyzją — utratą pieniędzy lub czasu", scores: { Mędrzec: 3, Władca: 1 } },
      { label: "Poczucie, że jest sam z problemem — nikt nie rozumie", scores: { Opiekun: 3, "Zw. Człowiek": 1 } },
      { label: "Frustracja, że wciąż nie osiąga tego, do czego dąży", scores: { Bohater: 3 } },
      { label: "Poczucie, że jego życie jest zbyt szare, przewidywalne i bez radości", scores: { Odkrywca: 2, Kochanek: 1, Błazen: 1 } },
    ],
  },
  {
    id: "c7",
    text: "Twój idealny klient staje się lojalnym ambasadorem marki gdy:",
    options: [
      { label: "Osiąga z Tobą mierzalne wyniki, których nie osiągał wcześniej", scores: { Bohater: 3, Mędrzec: 1 } },
      { label: "Czuje, że należy do czegoś ważnego — do plemienia z wartościami", scores: { Buntownik: 3, "Zw. Człowiek": 1 } },
      { label: "Marka go zaskakuje pozytywnie — ciągle jest coś nowego", scores: { Odkrywca: 3 } },
      { label: "Marka staje się częścią jego stylu życia — z dystansem i humorem", scores: { Błazen: 3, Kochanek: 1 } },
    ],
  },
  {
    id: "c8",
    text: "Twój idealny klient najchętniej poleca Cię znajomym gdy może powiedzieć:",
    options: [
      { label: "To najlepsi w tym, co robią — standard nie do pobicia", scores: { Władca: 3 } },
      { label: "Te ludzie naprawdę dbają o klienta, poczujesz różnicę", scores: { Opiekun: 3 } },
      { label: "Dzięki nim osiągnęłam wyniki, które miałam od dawna na liście", scores: { Bohater: 3 } },
      { label: "To nie jest typowa firma — coś tu jest inaczej, musisz to przeżyć", scores: { Czarodziej: 3, Buntownik: 1 } },
    ],
  },
  {
    id: "c9",
    text: "W kontakcie z marką Twój idealny klient ceni najbardziej:",
    options: [
      { label: "Ekspertyzę — wiedza i kompetencja są dla niego niezbędne", scores: { Mędrzec: 3 } },
      { label: "Efektywność — nie lubi tracić czasu, idzie do sedna", scores: { Bohater: 2, Władca: 2 } },
      { label: "Autentyczność — chce czuć, że za marką stoją prawdziwi ludzie z poczuciem humoru", scores: { Buntownik: 2, Stwórca: 1, Błazen: 1 } },
      { label: "Estetykę i rzemiosło — sposób w jaki marka wygląda i mówi ma dla niego znaczenie", scores: { Stwórca: 3, Kochanek: 1 } },
    ],
  },
  {
    id: "c10",
    text: "Co sprawi, że Twój idealny klient w końcu podejmie decyzję (jeśli się waha)?",
    options: [
      { label: "Jeden mocny dowód (case study, liczba, referencja) który usuwa wątpliwość", scores: { Mędrzec: 3, Bohater: 1 } },
      { label: "Rozmowa z prawdziwym, ciepłym człowiekiem, który zrozumie jego sytuację", scores: { Opiekun: 3, "Zw. Człowiek": 1, Niewinny: 1 } },
      { label: "Świadomość, że inni z jego środowiska już to wybrali", scores: { Władca: 2, "Zw. Człowiek": 2 } },
      { label: "Poczucie, że jeśli nie teraz, to straci coś wyjątkowego", scores: { Kochanek: 2, Odkrywca: 2 } },
    ],
  },
  {
    id: "c11",
    text: "Twój idealny klient podchodzi do zmiany i nowości w taki sposób, że:",
    options: [
      { label: "Aktywnie jej szuka — nuda i rutyna są dla niego porażką", scores: { Odkrywca: 3, Błazen: 2 } },
      { label: "Akceptuje ją, gdy widzi twardy dowód że przyniesie wyniki", scores: { Bohater: 2, Mędrzec: 2 } },
      { label: "Boi się jej — potrzebuje poczucia, że wszystko będzie dobrze", scores: { Niewinny: 3, Opiekun: 1 } },
      { label: "Chce jej głęboko, jeśli oznacza fundamentalną przemianę", scores: { Czarodziej: 3 } },
    ],
  },
  {
    id: "c12",
    text: "Twój idealny klient myśląc o sobie, identyfikuje się z obrazem:",
    options: [
      { label: "Kogoś kto buduje coś własnego, trwałego, autorskiego z dystansem do mainstreamu", scores: { Stwórca: 3, Odkrywca: 1, Błazen: 1 } },
      { label: "Kogoś kto walczy o lepszą wersję siebie każdego dnia", scores: { Bohater: 3 } },
      { label: "Kogoś kto stoi po właściwej stronie i nie idzie na kompromis", scores: { Buntownik: 3 } },
      { label: "Zwykłego człowieka, który chce żyć uczciwie, prosto i dobrze", scores: { "Zw. Człowiek": 3, Niewinny: 2 } },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MACIERZ DOPASOWANIA
// ─────────────────────────────────────────────────────────────────────────────
const MATCH_MATRIX = {
  "Władca-Władca":        { score: 5, desc: "Idealne lustro — wspólny język standardu i doskonałości. Klient rozumie wartość bez tłumaczenia." },
  "Władca-Mędrzec":       { score: 4, desc: "Silny rezonans — Mędrzec szuka eksperta który potwierdza standard. Marka musi być bezbłędna merytorycznie." },
  "Władca-Bohater":       { score: 4, desc: "Dobra para — Bohater chce narzędzi od najlepszych. Marka Władcy daje poczucie elitarnego wyposażenia." },
  "Władca-Kochanek":      { score: 3, desc: "Możliwy segment luksusowy — Kochanek kupuje prestiż. Ale Władca musi być estetycznie bezskazitelny." },
  "Władca-Buntownik":     { score: 1, desc: "Konflikt fundamentalny — Buntownik odrzuca hierarchię, Władca ją ustanawia. Trudna komunikacja." },
  "Władca-Błazen":        { score: 1, desc: "Napięcie tonalne — Władca jest powagą, Błazen jej zaprzeczeniem. Ryzyko odebrania marki jako pretensjonalnej." },
  "Władca-Odkrywca":      { score: 2, desc: "Słaby rezonans — Odkrywca ucieka od struktur, Władca je tworzy. Możliwy tylko w luksusowym kontekście podróży." },
  "Władca-Opiekun":       { score: 3, desc: "Umiarkowany — Opiekun szuka stabilności, Władca ją daje. Ale brak ciepła może blokować decyzję." },
  "Władca-Niewinny":      { score: 2, desc: "Trudne — Niewinny szuka prostoty, Władca kompleksowości i standardu. Możliwy w edukacji dzieci." },
  "Władca-Czarodziej":    { score: 3, desc: "Możliwy — Czarodziej chce zmiany, a Władca daje poczucie że jest dostęp do czegoś wyjątkowego." },
  "Władca-Zw. Człowiek":  { score: 1, desc: "Konflikt wartości — Zwykły Człowiek odrzuca hierarchię i elitaryzm który Władca reprezentuje." },
  "Władca-Stwórca":       { score: 3, desc: "Możliwy — Stwórca szuka narzędzi najwyższej jakości. Ale Władca nie może ingerować w autonomię twórczą." },
  "Opiekun-Opiekun":      { score: 5, desc: "Idealne lustro — głębokie zaufanie i poczucie bycia zaopiekowanym. Najsilniejsza możliwa lojalność." },
  "Opiekun-Niewinny":     { score: 5, desc: "Naturalna para — Niewinny potrzebuje ochrony i troski którą Opiekun daje. Łatwa, ciepła relacja." },
  "Opiekun-Zw. Człowiek": { score: 4, desc: "Silny rezonans — Zwykły Człowiek ceni relację i szczerość. Opiekun daje poczucie prawdziwej troski." },
  "Opiekun-Błazen":       { score: 3, desc: "Możliwy — Błazen ceni autentyczność, Opiekun ją ma. Ale Błazen może czuć zbyt dużo powagi." },
  "Opiekun-Kochanek":     { score: 3, desc: "Możliwy gdy kontekstem jest dar dla bliskich — Opiekun-marka obsługuje Kochanka kupującego dla innych." },
  "Opiekun-Bohater":      { score: 2, desc: "Napięcie — Bohater chce wygrywać, Opiekun chroni. Możliwy gdy produkt chroni w walce." },
  "Opiekun-Mędrzec":      { score: 3, desc: "Umiarkowany — Mędrzec szuka danych, Opiekun daje ciepło. Brakuje racjonalnego uzasadnienia." },
  "Opiekun-Władca":       { score: 3, desc: "Umiarkowany — Opiekun szuka stabilności, Władca ją daje. Ale brak ciepła może blokować decyzję." },
  "Opiekun-Odkrywca":     { score: 2, desc: "Słaby — Odkrywca szuka wolności, Opiekun bezpieczeństwa. Możliwy w ubezpieczeniach podróżnych." },
  "Opiekun-Buntownik":    { score: 2, desc: "Napięcie — Buntownik nie chce być pod opieką. Możliwy gdy marka chroni wartości które Buntownik wyznaje." },
  "Opiekun-Czarodziej":   { score: 2, desc: "Słaby bezpośredni rezonans — ale możliwy gdy transformacja jest rozumiana jako troska." },
  "Opiekun-Stwórca":      { score: 3, desc: "Możliwy — Stwórca potrzebuje bezpiecznej przestrzeni do tworzenia. Opiekun jako baza." },
  "Mędrzec-Mędrzec":      { score: 5, desc: "Idealne lustro — wspólny język analizy i dowodów. Decyzja oparta na danych, nie emocjach." },
  "Mędrzec-Bohater":      { score: 4, desc: "Silny — Bohater chce narzędzi które działają. Mędrzec dostarcza dowody które usuwają wątpliwości." },
  "Mędrzec-Władca":       { score: 4, desc: "Silny rezonans — Władca szuka eksperta by potwierdzić decyzję na najwyższym poziomie." },
  "Mędrzec-Stwórca":      { score: 4, desc: "Naturalna para — Stwórca szuka wiedzy by tworzyć lepiej. Mędrzec jako mistrz warsztatu." },
  "Mędrzec-Buntownik":    { score: 3, desc: "Możliwy — Buntownik szanuje intelektualną substancję. Musi być prawdziwa ekspertyza, nie fasada." },
  "Mędrzec-Odkrywca":     { score: 3, desc: "Możliwy — Odkrywca docenia wiedzę jako przewodnik po nowym. Ale nie może być zbyt akademicki." },
  "Mędrzec-Czarodziej":   { score: 3, desc: "Możliwy — Czarodziej szuka mechanizmu przemiany. Mędrzec może go dostarczyć gdy wyjaśnia dlaczego." },
  "Mędrzec-Kochanek":     { score: 2, desc: "Trudny — Kochanek decyduje zmysłami, Mędrzec danymi. Możliwy tylko gdy piękno ma naukowe uzasadnienie." },
  "Mędrzec-Błazen":       { score: 2, desc: "Napięcie — Błazen ma dystans do powagi Mędrca. Możliwy w edutainment." },
  "Mędrzec-Niewinny":     { score: 2, desc: "Trudny — Niewinny ufa prostocie, Mędrzec złożoności. Możliwy gdy wiedza jest podana prosto." },
  "Mędrzec-Opiekun":      { score: 3, desc: "Możliwy — Opiekun szuka wiedzy by lepiej dbać. Ale potrzebuje ciepła nie tylko danych." },
  "Mędrzec-Zw. Człowiek": { score: 2, desc: "Słaby — Zwykły Człowiek nie chce czuć się gorszy. Mędrzec może być zbyt ekspercki." },
  "Bohater-Bohater":      { score: 5, desc: "Idealne lustro — wspólny język wyników i przekraczania granic. Najsilniejsza motywacja." },
  "Bohater-Władca":       { score: 4, desc: "Dobra para — Bohater chce narzędzi od najlepszych. Marka Władcy daje poczucie elitarnego wyposażenia." },
  "Bohater-Mędrzec":      { score: 4, desc: "Silny — Bohater chce wiedzy która daje przewagę. Mędrzec jako coach strategiczny." },
  "Bohater-Odkrywca":     { score: 3, desc: "Możliwy — Odkrywca widzi nowe terytoria jako wyzwanie. Ale jego motywacja jest szersza niż zwycięstwo." },
  "Bohater-Buntownik":    { score: 3, desc: "Możliwy — Buntownik walczy ze systemem, Bohater z przeszkodami. Wspólna energia działania." },
  "Bohater-Stwórca":      { score: 3, desc: "Możliwy — Stwórca traktuje tworzenie jako formę mistrzostwa i przekraczania granic." },
  "Bohater-Opiekun":      { score: 2, desc: "Słaby — Opiekun chroni, Bohater ryzykuje. Możliwy gdy Bohater walczy dla innych." },
  "Bohater-Kochanek":     { score: 2, desc: "Trudny — Kochanek intensywnie przeżywa, Bohater intensywnie działa. Różne energie." },
  "Bohater-Błazen":       { score: 2, desc: "Napięcie — Bohater jest poważny w dążeniu do celu. Błazen traktuje to z dystansem." },
  "Bohater-Czarodziej":   { score: 4, desc: "Silny — Bohater szuka przeskoku jakościowego. Czarodziej obiecuje transformację przez narzędzie." },
  "Bohater-Niewinny":     { score: 1, desc: "Konflikt — Niewinny unika konfrontacji, Bohater jej szuka. Trudne połączenie." },
  "Bohater-Zw. Człowiek": { score: 2, desc: "Słaby — Zwykły Człowiek nie aspiruje do dominacji. Bohater go może przytłaczać energią." },
  "Buntownik-Buntownik":  { score: 5, desc: "Idealne lustro — wspólna tożsamość oporu. Najsilniejsza lojalność przez wartości." },
  "Buntownik-Odkrywca":   { score: 4, desc: "Silny — obaj odrzucają konformizm. Odkrywca szuka wolności, Buntownik rewolucji — bliska energia." },
  "Buntownik-Błazen":     { score: 4, desc: "Naturalna para — subwersja i humor jako wspólne narzędzia. Razem rozbrajają powagę systemu." },
  "Buntownik-Bohater":    { score: 3, desc: "Możliwy — Bohater walczy z przeszkodami, Buntownik z systemem. Wspólna energia działania." },
  "Buntownik-Stwórca":    { score: 3, desc: "Możliwy — Stwórca tworzy nową rzeczywistość, Buntownik obala starą. Komplementarne kierunki." },
  "Buntownik-Czarodziej":  { score: 3, desc: "Możliwy — obaj chcą zmiany. Buntownik przez opór, Czarodziej przez transformację." },
  "Buntownik-Mędrzec":    { score: 3, desc: "Możliwy — Buntownik szanuje intelektualną substancję. Musi być prawdziwa ekspertyza, nie fasada." },
  "Buntownik-Kochanek":   { score: 2, desc: "Trudny — Kochanek jest intensywny ale nie rewolucyjny. Buntownik może być zbyt twardy." },
  "Buntownik-Władca":     { score: 1, desc: "Konflikt fundamentalny — wartości antytetyczne." },
  "Buntownik-Opiekun":    { score: 2, desc: "Napięcie — Buntownik nie chce być chroniony. Możliwy gdy marka chroni wartości które Buntownik wyznaje." },
  "Buntownik-Niewinny":   { score: 1, desc: "Konflikt — Niewinny szuka bezpieczeństwa, Buntownik go rozbija." },
  "Buntownik-Zw. Człowiek": { score: 3, desc: "Możliwy — obaj odrzucają elitaryzm. Ale Zwykły Człowiek nie chce walki, tylko autentyczności." },
  "Odkrywca-Odkrywca":    { score: 5, desc: "Idealne lustro — wspólna filozofia życia jako podróży odkrywczej." },
  "Odkrywca-Buntownik":   { score: 4, desc: "Silny — obaj cenią wolność i odrzucają konformizm. Energia ruchu i zmiany." },
  "Odkrywca-Stwórca":     { score: 4, desc: "Silny — tworzenie jako forma odkrywania nowych terytoriów. Komplementarna energia." },
  "Odkrywca-Kochanek":    { score: 3, desc: "Możliwy — nowe doświadczenie przez pryzmat zmysłowego odkrycia." },
  "Odkrywca-Błazen":      { score: 3, desc: "Możliwy — lekkość i otwartość jako wspólna baza." },
  "Odkrywca-Czarodziej":  { score: 3, desc: "Możliwy — transformacja jako forma odkrycia nowego siebie." },
  "Odkrywca-Bohater":     { score: 3, desc: "Możliwy — granice do przekroczenia jako wspólny motyw." },
  "Odkrywca-Mędrzec":     { score: 3, desc: "Możliwy — wiedza jako mapa odkrycia." },
  "Odkrywca-Opiekun":     { score: 2, desc: "Słaby — Opiekun bezpieczeństwo, Odkrywca ryzyko." },
  "Odkrywca-Władca":      { score: 2, desc: "Trudny — Odkrywca ucieka od struktur, Władca je tworzy." },
  "Odkrywca-Niewinny":    { score: 2, desc: "Możliwy — wspólny optymizm. Ale Niewinny szuka bezpieczeństwa, Odkrywca nieznanego." },
  "Odkrywca-Zw. Człowiek": { score: 2, desc: "Słaby — Zwykły Człowiek ceni zakorzenienie, Odkrywca ciągły ruch." },
  "Kochanek-Kochanek":    { score: 5, desc: "Idealne lustro — wspólna waluta intensywnego doświadczenia. Najwyższe oczekiwania estetyczne." },
  "Kochanek-Odkrywca":    { score: 3, desc: "Możliwy — nowe doświadczenie zmysłowe jako forma odkrycia." },
  "Kochanek-Błazen":      { score: 2, desc: "Trudny — intensywność Kochanka i lekkość Błazna są trudne do pogodzenia." },
  "Kochanek-Niewinny":    { score: 3, desc: "Możliwy — piękno jako czyste doświadczenie." },
  "Kochanek-Stwórca":     { score: 3, desc: "Możliwy — estetyczna jakość jako wspólna wartość." },
  "Kochanek-Opiekun":     { score: 3, desc: "Możliwy — miłość wyrażona przez piękne gesty." },
  "Kochanek-Władca":      { score: 3, desc: "Możliwy w segmencie luksusowym — Władca jako nabywca estetyki premium." },
  "Kochanek-Buntownik":   { score: 2, desc: "Trudny — intensywność Kochanka różna od twardości Buntownika." },
  "Kochanek-Bohater":     { score: 2, desc: "Trudny — różne energie intensywności: zmysłowa vs. walki." },
  "Kochanek-Mędrzec":     { score: 2, desc: "Trudny — Mędrzec analizuje, Kochanek czuje." },
  "Kochanek-Czarodziej":  { score: 3, desc: "Możliwy — transformacja jako głęboka zmiana zmysłowego doświadczenia życia." },
  "Kochanek-Zw. Człowiek": { score: 2, desc: "Trudny — estetyczny elitaryzm Kochanka odpycha Zwykłego Człowieka." },
  "Błazen-Błazen":        { score: 5, desc: "Idealne lustro — wspólny język humoru i dystansu do powagi. Najlepsza możliwa relacja." },
  "Błazen-Buntownik":     { score: 4, desc: "Silny — subwersja przez śmiech i przez opór. Wspólna energia rozbrajania systemu." },
  "Błazen-Zw. Człowiek":  { score: 4, desc: "Silny — autentyczność i brak pretensji jako wspólna wartość." },
  "Błazen-Odkrywca":      { score: 3, desc: "Możliwy — lekkość i otwartość jako wspólna baza." },
  "Błazen-Niewinny":      { score: 3, desc: "Możliwy — radość jako wspólna waluta." },
  "Błazen-Opiekun":       { score: 3, desc: "Możliwy — autentyczność łączy." },
  "Błazen-Mędrzec":       { score: 2, desc: "Napięcie — Mędrzec szanuje wiedzę, Błazen jej dystans." },
  "Błazen-Stwórca":       { score: 3, desc: "Możliwy — tworzenie z poczuciem humoru." },
  "Błazen-Władca":        { score: 1, desc: "Konflikt tonalny — Władca jest powagą, Błazen jej antytezą." },
  "Błazen-Bohater":       { score: 2, desc: "Napięcie — Bohater skupiony na celu. Błazen rozbija skupienie." },
  "Błazen-Czarodziej":    { score: 2, desc: "Trudny — powaga transformacji i lekkość Błazna." },
  "Błazen-Kochanek":      { score: 2, desc: "Trudny — intensywność Kochanka nie rezonuje z lekkością Błazna." },
  "Czarodziej-Czarodziej": { score: 5, desc: "Idealne lustro — wspólna wiara w możliwość transformacji. Najgłębsza możliwa relacja." },
  "Czarodziej-Bohater":   { score: 4, desc: "Silny — Bohater szuka przeskoku jakościowego. Czarodziej dostarcza mechanizm transformacji." },
  "Czarodziej-Odkrywca":  { score: 3, desc: "Możliwy — nowa rzeczywistość po przemianie jako nowe terytorium." },
  "Czarodziej-Mędrzec":   { score: 3, desc: "Możliwy — transformacja przez głębsze rozumienie." },
  "Czarodziej-Stwórca":   { score: 4, desc: "Silny — tworzenie jako forma transformacji rzeczywistości." },
  "Czarodziej-Buntownik":  { score: 3, desc: "Możliwy — obaj chcą zmiany. Buntownik przez opór, Czarodziej przez przemianę." },
  "Czarodziej-Kochanek":  { score: 3, desc: "Możliwy — transformacja jako głęboka zmiana doświadczenia zmysłowego życia." },
  "Czarodziej-Opiekun":   { score: 2, desc: "Słaby bezpośredni rezonans — ale możliwy gdy transformacja służy trosce o innych." },
  "Czarodziej-Władca":    { score: 3, desc: "Możliwy — Czarodziej chce zmiany, a Władca daje poczucie że jest dostęp do czegoś wyjątkowego." },
  "Czarodziej-Niewinny":  { score: 1, desc: "Konflikt — Niewinny boi się zmiany. Transformacja Czarodzieja go niepokoi." },
  "Czarodziej-Błazen":    { score: 2, desc: "Trudny — powaga transformacji i lekkość Błazna." },
  "Czarodziej-Zw. Człowiek": { score: 1, desc: "Konflikt — Zwykły Człowiek jest OK taki jaki jest. Transformacja sugeruje że nie jest." },
  "Zw. Człowiek-Zw. Człowiek": { score: 5, desc: "Idealne lustro — autentyczność i przynależność jako rdzeń relacji. Najsilniejsza wspólnota." },
  "Zw. Człowiek-Opiekun":  { score: 4, desc: "Silny — wspólna wartość relacji i szczerości. Zwykły Człowiek czuje troskę Opiekuna." },
  "Zw. Człowiek-Błazen":   { score: 4, desc: "Silny — autentyczność i brak pretensji jako wspólna baza." },
  "Zw. Człowiek-Niewinny": { score: 4, desc: "Silny — prostota i bezpieczeństwo jako wspólne wartości." },
  "Zw. Człowiek-Buntownik": { score: 3, desc: "Możliwy — obaj odrzucają elitaryzm. Ale Zwykły Człowiek nie chce walki." },
  "Zw. Człowiek-Odkrywca": { score: 2, desc: "Słaby — Zwykły Człowiek ceni zakorzenienie, Odkrywca ciągły ruch." },
  "Zw. Człowiek-Stwórca":  { score: 3, desc: "Możliwy — tworzenie jako część codzienności." },
  "Zw. Człowiek-Mędrzec":  { score: 2, desc: "Trudny — Mędrzec może być zbyt ekspercki dla Zwykłego Człowieka." },
  "Zw. Człowiek-Bohater":  { score: 2, desc: "Słaby — aspiracje Bohatera są za wysokie dla Zwykłego Człowieka." },
  "Zw. Człowiek-Kochanek": { score: 2, desc: "Trudny — estetyczny elitaryzm Kochanka odpycha Zwykłego Człowieka." },
  "Zw. Człowiek-Władca":   { score: 1, desc: "Konflikt wartości — hierarchia vs. równość." },
  "Zw. Człowiek-Czarodziej": { score: 1, desc: "Konflikt — transformacja sugeruje że coś jest nie tak z tym jaki jesteś." },
  "Niewinny-Niewinny":    { score: 5, desc: "Idealne lustro — wiara w prostotę i dobroć. Najcieplejsza możliwa relacja." },
  "Niewinny-Opiekun":     { score: 5, desc: "Naturalna para — Niewinny potrzebuje troski, Opiekun ją daje." },
  "Niewinny-Zw. Człowiek": { score: 4, desc: "Silny — wspólna prostota i autentyczność bez pretensji." },
  "Niewinny-Błazen":      { score: 3, desc: "Możliwy — radość jako wspólna waluta. Ale Błazen jest bardziej cyniczny." },
  "Niewinny-Kochanek":    { score: 3, desc: "Możliwy — piękno jako czyste doświadczenie." },
  "Niewinny-Odkrywca":    { score: 2, desc: "Napięcie — Niewinny szuka bezpieczeństwa, Odkrywca nieznanego." },
  "Niewinny-Mędrzec":     { score: 2, desc: "Trudny — Niewinny ufa prostocie, Mędrzec złożoności." },
  "Niewinny-Stwórca":     { score: 2, desc: "Możliwy — tworzenie jako radość i zabawa." },
  "Niewinny-Bohater":     { score: 1, desc: "Konflikt — Niewinny unika konfrontacji." },
  "Niewinny-Buntownik":   { score: 1, desc: "Konflikt — bunt burzy bezpieczeństwo Niewinnego." },
  "Niewinny-Władca":      { score: 2, desc: "Możliwy w kontekście edukacji dzieci lub ochrony." },
  "Niewinny-Czarodziej":  { score: 1, desc: "Konflikt — transformacja Czarodzieja niepokoi Niewinnego." },
  "Stwórca-Stwórca":      { score: 5, desc: "Idealne lustro — wspólna filozofia tworzenia. Wzajemny szacunek dla procesu i rzemiosła." },
  "Stwórca-Odkrywca":     { score: 4, desc: "Silny — tworzenie jako forma odkrywania nowych terytoriów." },
  "Stwórca-Mędrzec":      { score: 4, desc: "Silny — wiedza jako fundament warsztatu. Mędrzec jako mistrz rzemiosła." },
  "Stwórca-Bohater":      { score: 3, desc: "Możliwy — tworzenie jako forma mistrzostwa i przekraczania granic." },
  "Stwórca-Czarodziej":   { score: 4, desc: "Silny — tworzenie jako transformacja rzeczywistości przez artefakt." },
  "Stwórca-Buntownik":    { score: 3, desc: "Możliwy — tworzenie nowej rzeczywistości przez obalanie starej." },
  "Stwórca-Błazen":       { score: 3, desc: "Możliwy — tworzenie z poczuciem humoru i dystansem do powagi." },
  "Stwórca-Kochanek":     { score: 3, desc: "Możliwy — estetyczna jakość jako wspólna wartość." },
  "Stwórca-Opiekun":      { score: 3, desc: "Możliwy — Opiekun jako bezpieczna przestrzeń do tworzenia." },
  "Stwórca-Władca":       { score: 3, desc: "Możliwy — Stwórca szuka narzędzi najwyższej jakości." },
  "Stwórca-Niewinny":     { score: 2, desc: "Możliwy — tworzenie jako radosna ekspresja." },
  "Stwórca-Zw. Człowiek": { score: 3, desc: "Możliwy — tworzenie jako część codzienności." },
};

function getMatchKey(brand, client) {
  const k1 = `${brand}-${client}`;
  const k2 = `${client}-${brand}`;
  return MATCH_MATRIX[k1] || MATCH_MATRIX[k2] || { score: 2, desc: "Brak szczegółowych danych dla tej pary — analiza wymaga pogłębienia na sesji strategicznej." };
}

// ─────────────────────────────────────────────────────────────────────────────
// STATYCZNE REKOMENDACJE KOMUNIKACYJNE — szczegółowe per para
// ─────────────────────────────────────────────────────────────────────────────
const COMM_STATIC = {
  "Władca-Władca":       { pain: "Mów o kontroli i standardzie. Klient boi się chaosu — pokaż jak eliminujesz zmienne.", cta: "Bezpośrednie, oparte na autorytecie: Zarezerwuj miejsce / Dołącz do standardu.", tone: "Rzeczowy, pewny, bez zbędnych słów. Zero infantylizmu.", loyalty: "Potwierdzaj status klienta. Ekskluzywny dostęp, first-mover advantage, VIP treatment.", avoid: "Unikaj: zbyt casual tonu, nadmiaru emocji, braku danych." },
  "Władca-Mędrzec":      { pain: "Klient boi się błędnej decyzji — daj mu dane, mechanizm, logikę wyboru.", cta: "Sprawdź metodologię / Pobierz analizę — niech decyzja wynika z dowodów.", tone: "Ekspercki, precyzyjny, z substancją merytoryczną.", loyalty: "Dostarczaj wiedzę która potwierdza jego wybór jako racjonalny i najlepszy.", avoid: "Unikaj: pustych haseł, braku uzasadnienia, emocjonalnego języka bez danych." },
  "Władca-Bohater":      { pain: "Klient chce wygrywać — pokaż jak Twoja marka daje mu przewagę nad konkurencją.", cta: "Zacznij wygrywać / Dołącz do najlepszych — akcja i wynik.", tone: "Pewny, zorientowany na wynik, z dowodem skuteczności.", loyalty: "Mierzalne wyniki, rankingi, dowody że klient z Tobą jest lepszy niż bez Ciebie.", avoid: "Unikaj: soft language, braku konkretnych liczb, narracji o procesie zamiast wyniku." },
  "Mędrzec-Mędrzec":     { pain: "Ból: zła decyzja przez brak wiedzy. Mów: Zanim zdecydujesz — przeczytaj to.", cta: "Pobierz analizę / Sprawdź metodologię / Umów konsultację diagnostyczną.", tone: "Analityczny, spokojny, oparty na faktach. Unikaj hype'u.", loyalty: "Regularnie dostarczaj wiedzę która wyprzedza rynek. Klient zostaje bo uczysz go więcej.", avoid: "Unikaj: emocjonalnego języka, niepopartych twierdzeń, zbyt uproszczonych komunikatów." },
  "Mędrzec-Bohater":     { pain: "Klient chce wyników — daj mu wiedzę jako narzędzie przewagi.", cta: "Zdobądź przewagę / Sprawdź co działa — wiedza jako dźwignia wygranej.", tone: "Ekspercki z energią działania. Nie tylko teoria — konkretna aplikacja.", loyalty: "Pokazuj jak wiedza którą dajesz przekłada się na mierzalne wyniki klienta.", avoid: "Unikaj: zbyt akademickiego tonu, braku praktycznych przykładów." },
  "Mędrzec-Stwórca":     { pain: "Klient chce tworzyć lepiej — daj mu wiedzę jako fundament rzemiosła.", cta: "Opanuj metodę / Pogłęb warsztat — wiedza jako narzędzie twórcze.", tone: "Mistrz do ucznia — szacunek dla procesu twórczego z substancją merytoryczną.", loyalty: "Klient pozostaje lojalny gdy czuje że Twoja wiedza podnosi poziom jego pracy.", avoid: "Unikaj: narzucania jednej drogi, ignorowania autonomii twórczej klienta." },
  "Opiekun-Opiekun":     { pain: "Klient boi się że coś złego spotka jego bliskich lub sam zostanie bez wsparcia.", cta: "Zadbaj o siebie i swoich / Jesteśmy tu gdy nas potrzebujesz.", tone: "Ciepły, empatyczny, bliski. Mów my, nie ja. Mów o relacji, nie o produkcie.", loyalty: "Regularny kontakt, pamiętanie o ważnych momentach, autentyczna troska.", avoid: "Unikaj: zimnego, transakcyjnego języka, braku osobistego tonu." },
  "Opiekun-Niewinny":    { pain: "Klient boi się że świat jest za trudny. Pokaż że z Tobą jest prosto i bezpiecznie.", cta: "Zrobimy to razem / Jesteś w dobrych rękach — ciepłe, bez presji.", tone: "Łagodny, prosty, pełen nadziei. Żadnego straszenia problemami.", loyalty: "Prostota, przewidywalność, poczucie że nic złego się nie przydarzy.", avoid: "Unikaj: złożonego języka, straszenia konsekwencjami, presji decyzyjnej." },
  "Bohater-Bohater":     { pain: "Strach przed przeciętnością — klient nie może sobie pozwolić na plateau.", cta: "Przekrocz swój rekord / Sprawdź ile możesz osiągnąć — wyzwanie jako CTA.", tone: "Energiczny, zorientowany na wynik, bezkompromisowy.", loyalty: "Dostarcz wyniki, dokumentuj progres, świętuj zwycięstwa klienta.", avoid: "Unikaj: miękkiego języka, narracji o procesie bez wyników, zbyt dużo emocji." },
  "Bohater-Czarodziej":  { pain: "Klient chce fundamentalnej zmiany — nie drobnej poprawy.", cta: "Przekształć swoje wyniki / Zacznij od zera — tym razem inaczej.", tone: "Inspirujący z energią działania. Transformacja przez konkretne kroki.", loyalty: "Klient zostaje gdy czuje że z Tobą przekracza własne granice.", avoid: "Unikaj: małych obietnic, braku wizji co jest możliwe." },
  "Buntownik-Buntownik": { pain: "Klient nie chce być częścią systemu który go uniformizuje.", cta: "Dołącz do tych którzy myślą inaczej / To nie jest dla każdego.", tone: "Odważny, bezpośredni, z wyraźnym punktem widzenia. Mów co myślisz.", loyalty: "Klient zostaje gdy marka konsekwentnie stoi po jego stronie wartości.", avoid: "Unikaj: kompromisów w tonie, zbyt miękkiego języka, udawania neutralności." },
  "Buntownik-Odkrywca":  { pain: "Klient odrzuca rutynę i szuka tego co inne.", cta: "Odkryj inną drogę / Wyjdź poza standard — wolność jako obietnica.", tone: "Prowokacyjny ale otwarty. Nie narzucaj — zapraszaj do odkrycia.", loyalty: "Ciągle dostarczaj coś nowego i nieoczekiwanego.", avoid: "Unikaj: przewidywalności, standardowych formatów, zbyt agresywnego buntu." },
  "Odkrywca-Odkrywca":   { pain: "Klient boi się stagnacji i rutyny bardziej niż ryzyka.", cta: "Odkryj co możliwe / Ruszaj dalej — horyzont jako obietnica.", tone: "Lekki, otwarty, pełen możliwości. Nie sprzedaj — zapraszaj.", loyalty: "Ciągle pokazuj co nowe, nieodkryte, czekające na eksplorację.", avoid: "Unikaj: ciężkiego tonu, ograniczeń i zasad, zbyt strukturalnego języka." },
  "Czarodziej-Czarodziej": { pain: "Klient czuje się uwięziony w starym stanie — szuka fundamentalnej przemiany.", cta: "Zacznij transformację / To moment zmiany — nie odkładaj.", tone: "Wizjonerski, głęboki, z poczuciem wyjątkowości momentu.", loyalty: "Klient zostaje gdy faktycznie doświadcza przemiany — nie tylko obietnicy.", avoid: "Unikaj: powierzchownych obietnic, zbyt szybkich rozwiązań, braku głębi." },
  "Czarodziej-Stwórca":  { pain: "Klient chce tworzyć rzeczy które transformują — nie tylko produkować.", cta: "Stwórz coś co zmieni zasady gry — tworzenie jako akt transformacji.", tone: "Inspirujący, głęboki, z szacunkiem dla procesu twórczego.", loyalty: "Klient zostaje gdy czuje że Twoje narzędzia/wiedza otwierają nowe możliwości twórcze.", avoid: "Unikaj: sprowadzania do technikaliów, ignorowania wizjonerskiego wymiaru pracy." },
  "Stwórca-Stwórca":     { pain: "Klient boi się mediokracji — tworzy po to by zostawić coś trwałego.", cta: "Stwórz coś niepodrabialnego / Twoje rzemiosło zasługuje na najlepsze narzędzia.", tone: "Z szacunkiem dla procesu, rzemiosła i wizji. Peer-to-peer, nie brand-to-consumer.", loyalty: "Jakość narzędzi i procesu. Klient zostaje gdy Twoje rozwiązania podnoszą poziom jego pracy.", avoid: "Unikaj: masowego języka, braku dbałości o szczegół, traktowania twórcy jak konsumenta." },
  "Kochanek-Kochanek":   { pain: "Klient boi się że nie będzie kochany, widziany, pożądany.", cta: "Poczuj różnicę / Doświadcz — CTA zmysłowe, nie transakcyjne.", tone: "Zmysłowy, bliski, poetycki. Każde słowo ma smakować.", loyalty: "Estetyczne doznanie na każdym punkcie styku. Klient zostaje bo to jego styl życia.", avoid: "Unikaj: suchego, informacyjnego języka, braku dbałości o estetykę komunikacji." },
  "Błazen-Błazen":       { pain: "Klient boi się nudy i przymusu powagi — chce lekkości.", cta: "Sprawdź to / No to lecimy — nieformalnie, z humorem, bez patosu.", tone: "Autentyczny, zabawny, z dystansem do siebie. Nigdy nie udaje powagi.", loyalty: "Klient zostaje bo z Tobą jest po prostu dobrze. Nie dramatyzuj — baw się.", avoid: "Unikaj: nadmiernej powagi, korporacyjnego języka, udawania że to coś ważnego." },
  "Zw. Człowiek-Zw. Człowiek": { pain: "Klient boi się wykluczenia i bycia traktowanym jak gorszy.", cta: "Dołącz do nas / Jesteś tu u siebie — przynależność, nie presja.", tone: "Zwykły, ciepły, bez hierarchii. Mów jak do sąsiada, nie jak do klienta.", loyalty: "Wspólnota i poczucie przynależności. Klient zostaje bo czuje się swój.", avoid: "Unikaj: elitarnego języka, podkreślania wyjątkowości, braku dostępności." },
};

function getCommRec(brand, client) {
  const key1 = `${brand}-${client}`;
  const key2 = `${client}-${brand}`;
  const static_rec = COMM_STATIC[key1] || COMM_STATIC[key2];
  const bd = ARCHETYPES[brand] || {};
  const cd = ARCHETYPES[client] || {};
  const match = getMatchKey(brand, client);

  if (static_rec) {
    return {
      painAddress: static_rec.pain,
      cta: static_rec.cta,
      tone: static_rec.tone,
      loyalty: static_rec.loyalty,
      avoid: static_rec.avoid,
    };
  }

  // fallback dynamiczny dla par bez statycznej rekomendacji
  return {
    painAddress: `Ból klienta (${cd.fear || "—"}) — adresuj przez język ${bd.tagline || brand}. Mów o tym jak klient będzie się czuł gdy ból zniknie.`,
    cta: match.score >= 4
      ? `Silny rezonans — CTA może być bezpośrednie. Klient ${client} reaguje na: pewność, konkret i brak owijania w bawełnę.`
      : match.score === 3
      ? `Umiarkowany rezonans — CTA wymaga pomostu. Najpierw zaadresuj ból klienta językiem ${client}, dopiero potem zaproś do działania głosem marki ${brand}.`
      : `Słaby rezonans — konieczny mostek komunikacyjny. Rozważ weryfikację czy klient idealny jest dobrze zdefiniowany.`,
    tone: `Ton: ${bd.tagline}. Słowa kluczowe marki: ${(bd.keywords || []).join(", ")}.`,
    loyalty: `Lojalność klienta budujesz przez: ${cd.loyalty}. Twoja marka dostarcza to przez: ${bd.loyalty}.`,
    avoid: `Unikaj języka który podkreśla: ${cd.fear?.toLowerCase() || "brak danych"}.`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORING
// ─────────────────────────────────────────────────────────────────────────────
function calcScores(answers, questions) {
  const scores = {};
  ARCH_KEYS.forEach(k => (scores[k] = 0));
  questions.forEach(q => {
    const ans = answers[q.id];
    if (ans !== undefined) {
      const opt = q.options[ans];
      if (opt?.scores) Object.entries(opt.scores).forEach(([k, v]) => { if (scores[k] !== undefined) scores[k] += v; });
    }
  });
  return scores;
}

function topTwo(scores) {
  return Object.entries(scores).sort((a, b) => b[1] - a[1]).slice(0, 2).map(e => e[0]);
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0A0A0C;
    --surface: #13131A;
    --surface2: #1A1A24;
    --border: #252530;
    --text: #F0EEE8;
    --muted: #7A7A88;
    --accent: #F59E0B;
    --accent2: #7B4FBF;
    --accent3: #5BA4CF;
    --radius: 14px;
    --font-display: 'Playfair Display', serif;
    --font-body: 'DM Sans', sans-serif;
  }

  body { background: var(--bg); color: var(--text); font-family: var(--font-body); min-height: 100vh; }

  .app { max-width: 880px; margin: 0 auto; padding: 48px 24px 96px; }

  /* HEADER */
  .header { text-align: center; margin-bottom: 64px; padding-top: 16px; position: relative; }
  .header::before {
    content: '';
    position: absolute;
    top: -40px; left: 50%; transform: translateX(-50%);
    width: 1px; height: 40px;
    background: linear-gradient(to bottom, transparent, var(--accent));
  }
  .header-logo { font-size: 11px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; display: flex; align-items: center; justify-content: center; gap: 8px; }
  .header-logo::before, .header-logo::after { content: ''; display: block; width: 32px; height: 1px; background: var(--accent); opacity: 0.4; }
  .header-title { font-family: var(--font-display); font-size: clamp(2.2rem, 5.5vw, 3.6rem); font-weight: 900; line-height: 1.05; margin-bottom: 6px; letter-spacing: -0.02em; }
  .header-title span { font-style: italic; color: var(--accent); }
  .header-sub { color: var(--muted); font-size: 14px; font-weight: 300; max-width: 480px; margin: 16px auto 0; line-height: 1.7; }

  /* STEPS */
  .steps { display: flex; gap: 0; margin-bottom: 52px; position: relative; }
  .steps::before { content:''; position:absolute; top:20px; left:20px; right:20px; height:1px; background: var(--border); z-index:0; }
  .step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 10px; position:relative; z-index:1; }
  .step-num { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; border: 1px solid var(--border); background: var(--bg); transition: all .3s; }
  .step-num.active { background: var(--accent); border-color: var(--accent); color: #000; box-shadow: 0 0 20px rgba(201,162,39,0.3); }
  .step-num.done { background: var(--surface2); border-color: var(--accent); color: var(--accent); }
  .step-label { font-size: 11px; color: var(--muted); text-align: center; max-width: 80px; line-height: 1.3; letter-spacing: 0.3px; }
  .step-label.active { color: var(--text); }

  /* MODULE CARD */
  .module { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 40px; margin-bottom: 24px; }
  .module-header { margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--border); }
  .module-eyebrow { font-size: 10px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: var(--accent); margin-bottom: 10px; }
  .module-title { font-family: var(--font-display); font-size: 1.5rem; margin-bottom: 8px; }
  .module-desc { color: var(--muted); font-size: 13px; line-height: 1.6; }

  /* PROGRESS */
  .progress-wrap { margin-bottom: 36px; }
  .progress-meta { display: flex; justify-content: space-between; font-size: 11px; color: var(--muted); margin-bottom: 8px; letter-spacing: 0.5px; }
  .progress-bar { height: 2px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .progress-fill { height: 100%; background: linear-gradient(90deg, var(--accent), #e8b830); border-radius: 99px; transition: width .5s ease; }

  /* QUESTION */
  .question { margin-bottom: 36px; padding-bottom: 36px; border-bottom: 1px solid var(--border); }
  .question:last-of-type { border-bottom: none; }
  .question-num { color: var(--accent); font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; display: block; margin-bottom: 10px; }
  .question-text { font-size: 15px; font-weight: 500; margin-bottom: 16px; line-height: 1.55; color: var(--text); }
  .options { display: grid; gap: 8px; }
  .option { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 14px 18px; cursor: pointer; font-size: 13.5px; line-height: 1.5; transition: all .2s; color: var(--text); text-align: left; font-family: var(--font-body); }
  .option:hover { border-color: rgba(201,162,39,0.5); background: rgba(201,162,39,0.04); }
  .option.selected { border-color: var(--accent); background: rgba(201,162,39,0.08); }

  /* OPEN TEXT */
  .open-text-section { margin-top: 36px; padding-top: 32px; border-top: 1px solid var(--border); }
  .open-text-label { font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--accent3); margin-bottom: 8px; display: block; }
  .open-text-hint { font-size: 12px; color: var(--muted); margin-bottom: 12px; line-height: 1.5; }
  .open-textarea { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; font-size: 13px; color: var(--text); font-family: var(--font-body); resize: vertical; min-height: 80px; outline: none; line-height: 1.6; transition: border-color .2s; }
  .open-textarea:focus { border-color: var(--accent3); }

  /* BUTTONS */
  .btn-row { display: flex; justify-content: space-between; align-items: center; margin-top: 40px; padding-top: 32px; border-top: 1px solid var(--border); gap: 12px; }
  .btn { padding: 13px 32px; border-radius: 8px; font-family: var(--font-body); font-size: 14px; font-weight: 600; cursor: pointer; transition: all .2s; border: none; letter-spacing: 0.3px; }
  .btn-primary { background: var(--accent); color: #000; }
  .btn-primary:hover { background: #d4a830; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(201,162,39,0.25); }
  .btn-primary:disabled { opacity: 0.35; cursor: not-allowed; transform: none; box-shadow: none; }
  .btn-ghost { background: transparent; color: var(--muted); border: 1px solid var(--border); }
  .btn-ghost:hover { border-color: var(--muted); color: var(--text); }
  .answered-count { font-size: 12px; color: var(--muted); }

  /* RESULTS */
  .results-header { text-align: center; margin-bottom: 52px; padding: 48px 0 0; }
  .results-eyebrow { font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: var(--accent); margin-bottom: 16px; }
  .results-title { font-family: var(--font-display); font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 700; margin-bottom: 8px; }
  .results-sub { color: var(--muted); font-size: 13px; }

  .result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
  .arch-card { border-radius: var(--radius); padding: 28px; border: 1px solid var(--border); position: relative; overflow: hidden; }
  .arch-card-label { font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 14px; }
  .arch-card-emoji { font-size: 2.2rem; margin-bottom: 10px; display: block; }
  .arch-card-name { font-family: var(--font-display); font-size: 1.7rem; font-weight: 700; margin-bottom: 6px; }
  .arch-card-tagline { font-size: 12px; font-weight: 400; margin-bottom: 18px; font-style: italic; }
  .arch-card-detail { font-size: 12px; line-height: 1.6; margin-bottom: 10px; }
  .arch-card-detail strong { display: block; margin-bottom: 3px; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; opacity: 0.7; }
  .arch-card-keywords { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 14px; }
  .keyword-tag { font-size: 10px; padding: 3px 10px; border-radius: 99px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); color: var(--muted); letter-spacing: 0.5px; }

  .secondary-note { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 16px 20px; margin-top: 12px; font-size: 13px; color: var(--muted); line-height: 1.6; }
  .secondary-note strong { color: var(--text); }

  /* SCORE BARS */
  .score-bars { margin-bottom: 4px; }
  .score-row { display: flex; align-items: center; gap: 10px; margin-bottom: 7px; }
  .score-name { font-size: 11.5px; min-width: 110px; color: var(--muted); }
  .score-track { flex:1; height: 4px; background: var(--border); border-radius:99px; overflow:hidden; }
  .score-fill { height:100%; border-radius:99px; transition: width 1s ease; }
  .score-val { font-size: 11px; min-width: 22px; text-align: right; color: var(--muted); }

  /* MATCH METER */
  .match-section { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 32px; margin-bottom: 20px; }
  .match-title { font-family: var(--font-display); font-size: 1.15rem; margin-bottom: 22px; }
  .match-meter { display: flex; align-items: center; gap: 16px; margin-bottom: 14px; }
  .meter-track { flex: 1; height: 6px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .meter-fill { height: 100%; border-radius: 99px; transition: width 1.2s ease; }
  .meter-score { font-size: 24px; font-weight: 700; min-width: 40px; text-align: right; font-family: var(--font-display); }
  .match-desc { font-size: 13.5px; color: var(--muted); line-height: 1.65; }

  /* COMM RECS */
  .comm-section { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 32px; margin-bottom: 20px; }
  .comm-title { font-family: var(--font-display); font-size: 1.15rem; margin-bottom: 22px; }
  .rec-item { border-left: 2px solid var(--accent); padding: 14px 18px; margin-bottom: 12px; background: rgba(201,162,39,0.04); border-radius: 0 10px 10px 0; }
  .rec-item.avoid { border-left-color: #C0392B; background: rgba(192,57,43,0.04); }
  .rec-label { font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
  .rec-label.red { color: #C0392B; }
  .rec-text { font-size: 13px; line-height: 1.65; color: var(--text); }

  /* OPEN TEXT RESULTS */
  .open-results { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 32px; margin-bottom: 20px; }
  .open-results-title { font-family: var(--font-display); font-size: 1.15rem; margin-bottom: 20px; }
  .open-result-block { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid var(--border); }
  .open-result-block:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
  .open-result-label { font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--accent3); margin-bottom: 8px; }
  .open-result-text { font-size: 13px; color: var(--text); line-height: 1.65; font-style: italic; padding: 12px 16px; background: var(--surface2); border-radius: 8px; border: 1px solid var(--border); }

  /* EMAIL SECTION */
  .email-section { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 32px; margin-bottom: 20px; }

  .restart-row { text-align: center; margin-top: 40px; }

  @media (max-width: 640px) {
    .result-grid { grid-template-columns: 1fr; }
    .app { padding: 28px 16px 64px; }
    .module { padding: 28px 24px; }
    .match-section, .comm-section, .open-results, .email-section { padding: 24px; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function ScoreBars({ scores, topArch }) {
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = sorted[0]?.[1] || 1;
  return (
    <div className="score-bars">
      {sorted.map(([name, val]) => (
        <div className="score-row" key={name}>
          <span className="score-name" style={{ color: name === topArch ? ARCHETYPES[name]?.accent : undefined }}>{name}</span>
          <div className="score-track">
            <div className="score-fill" style={{ width: `${(val / max) * 100}%`, background: name === topArch ? ARCHETYPES[name]?.accent : "#2A2A38" }} />
          </div>
          <span className="score-val">{val}</span>
        </div>
      ))}
    </div>
  );
}

function ArchCard({ arch, label }) {
  const d = ARCHETYPES[arch] || {};
  return (
    <div className="arch-card" style={{ background: `${d.color}20`, borderColor: `${d.accent}35` }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${d.accent}, ${d.accent}80)`, borderRadius: '14px 14px 0 0' }} />
      <div className="arch-card-label" style={{ color: d.accent }}>{label}</div>
      <span className="arch-card-emoji">{d.emoji}</span>
      <div className="arch-card-name">{arch}</div>
      <div className="arch-card-tagline" style={{ color: d.accent }}>„{d.tagline}"</div>
      <div className="arch-card-detail">
        <strong style={{ color: d.accent }}>Główny strach</strong>{d.fear}
      </div>
      <div className="arch-card-detail">
        <strong style={{ color: d.accent }}>Lojalność przez</strong>{d.loyalty}
      </div>
      {d.keywords && (
        <div className="arch-card-keywords">
          {d.keywords.map(k => <span key={k} className="keyword-tag">{k}</span>)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [phase, setPhase] = useState("brand");
  const [brandAnswers, setBrandAnswers] = useState({});
  const [clientAnswers, setClientAnswers] = useState({});
  const [brandOpenText, setBrandOpenText] = useState("");
  const [clientOpenText, setClientOpenText] = useState("");
  const [clientName, setClientName] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);
  const [gateEmail, setGateEmail] = useState("");
  const [gateSubmitted, setGateSubmitted] = useState(false);
  const [gateSending, setGateSending] = useState(false);
  const [gateError, setGateError] = useState("");
  const topRef = useRef(null);

  const currentAnswers = phase === "brand" ? brandAnswers : clientAnswers;
  const currentQ = phase === "brand" ? BRAND_QUESTIONS : CLIENT_QUESTIONS;
  const currentSet = phase === "brand" ? setBrandAnswers : setClientAnswers;

  const answered = currentQ.filter(q => currentAnswers[q.id] !== undefined).length;
  const total = currentQ.length;
  const allAnswered = answered === total;

  function scroll() { topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }

  function handleSelect(qid, idx) {
    currentSet(prev => ({ ...prev, [qid]: idx }));
  }

  function handleNext() {
    scroll();
    if (phase === "brand") setPhase("client");
    else setPhase("results");
  }

  function handleBack() {
    scroll();
    if (phase === "client") setPhase("brand");
  }

  function handleRestart() {
    setBrandAnswers({});
    setClientAnswers({});
    setBrandOpenText("");
    setClientOpenText("");
    setClientName("");
    setEmailStatus(null);
    setGateEmail("");
    setGateSubmitted(false);
    setGateSending(false);
    setGateError("");
    setPhase("brand");
    scroll();
  }

  const brandScores = calcScores(brandAnswers, BRAND_QUESTIONS);
  const clientScores = calcScores(clientAnswers, CLIENT_QUESTIONS);
  const [brandTop, brandSecond] = topTwo(brandScores);
  const [clientTop, clientSecond] = topTwo(clientScores);
  const match = getMatchKey(brandTop, clientTop);
  const comm = getCommRec(brandTop, clientTop);

  const matchColor = match.score >= 4 ? "#4CAF50" : match.score === 3 ? "#C9A227" : "#E74C3C";
  const matchLabel = match.score >= 4 ? "Silny rezonans" : match.score === 3 ? "Umiarkowany rezonans" : "Słaby rezonans — wymaga analizy";

  async function handleGateSubmit() {
    if (!gateEmail.trim() || !gateEmail.includes("@")) {
      setGateError("Podaj prawidłowy adres email.");
      return;
    }
    setGateSending(true);
    setGateError("");
    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          client_name: gateEmail,
          client_email: gateEmail,
          brand_archetype: brandTop,
          client_archetype: clientTop,
          match_score: String(match.score),
          match_label: matchLabel,
          match_description: match.desc,
          pain_address: comm.painAddress,
          cta_strategy: comm.cta,
          tone: comm.tone,
          loyalty: comm.loyalty,
        },
        EMAILJS_PUBLIC_KEY
      );
      console.log("EmailJS OK:", result.status, result.text);
    } catch (err) {
      console.error("EmailJS ERR:", err);
      setGateError("Wystąpił błąd wysyłania — otwórz konsolę (F12) i sprawdź szczegóły.");
      setGateSending(false);
      return;
    }
    setGateSubmitted(true);
    setGateSending(false);
  }

  async function handleSendReport() {
    if (!clientName.trim()) {
      alert("Wpisz imię lub nazwę marki klienta przed wysłaniem raportu.");
      return;
    }
    setEmailStatus("sending");
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          client_name: clientName,
          brand_archetype: brandTop,
          client_archetype: clientTop,
          match_score: String(match.score),
          match_description: match.desc,
          recommendations: [
            "ADRESOWANIE BÓLU:\n" + comm.painAddress,
            "STRATEGIA CTA:\n" + comm.cta,
            "TON KOMUNIKACJI:\n" + comm.tone,
            "BUDOWANIE LOJALNOŚCI:\n" + comm.loyalty,
            comm.avoid ? "CZEGO UNIKAĆ:\n" + comm.avoid : "",
            brandOpenText ? "OPIS MARKI (własny):\n" + brandOpenText : "",
            clientOpenText ? "OPIS KLIENTA (własny):\n" + clientOpenText : "",
          ].filter(Boolean).join("\n\n---\n\n"),
        },
        EMAILJS_PUBLIC_KEY
      );
      setEmailStatus("sent");
    } catch (err) {
      console.error(err);
      setEmailStatus("error");
    }
  }

  function handleDownloadPDF() {
    generatePrintPDF({
      brandTop, clientTop, brandSecond, clientSecond,
      brandScores, clientScores,
      match, matchLabel, matchColor,
      comm, ARCHETYPES,
      clientName, brandOpenText, clientOpenText,
      matchScore: match.score,
    });
  }

    // Ctrl+Shift+P — sekretny skrót PDF dla stratega (pomija bramkę)
  useEffect(() => {
    async function handleKeyPDF(e) {
      if (e.ctrlKey && e.shiftKey && e.key === "P" && phase === "results") {
        e.preventDefault();
        await handleDownloadPDF();
      }
    }
    window.addEventListener("keydown", handleKeyPDF);
    return () => window.removeEventListener("keydown", handleKeyPDF);
  }, [phase, brandTop, clientTop, match, comm, matchLabel, clientName, brandOpenText, clientOpenText]);

  const stepInfo = [
    { label: "Archetyp Marki", key: "brand" },
    { label: "Archetyp Klienta", key: "client" },
    { label: "Raport Rezonansu", key: "results" },
  ];

  return (
    <>
      <style>{css}</style>
      <div className="app" ref={topRef}>

        {/* HEADER */}
        <div className="header">
          <div className="header-logo">Selvus™</div>
          <h1 className="header-title">Diagnoza <span>Archetypów</span></h1>
          <p className="header-sub">Narzędzie diagnostyczne metodologii Selvus™ — identyfikacja Jaźni marki, archetypu klienta idealnego i siły ich rezonansu komunikacyjnego.</p>
        </div>

        {/* STEPS */}
        <div className="steps">
          {stepInfo.map((s, i) => {
            const phases = ["brand", "client", "results"];
            const currentIdx = phases.indexOf(phase);
            const isActive = phase === s.key;
            const isDone = i < currentIdx;
            return (
              <div className="step" key={s.key}>
                <div className={`step-num ${isActive ? "active" : isDone ? "done" : ""}`}>
                  {isDone ? "✓" : i + 1}
                </div>
                <span className={`step-label ${isActive ? "active" : ""}`}>{s.label}</span>
              </div>
            );
          })}
        </div>

        {/* BRAND MODULE */}
        {phase === "brand" && (
          <div className="module">
            <div className="module-header">
              <div className="module-eyebrow">Moduł A</div>
              <div className="module-title">Archetyp Marki</div>
              <p className="module-desc">Odpowiedz na pytania dotyczące marki którą diagnozujesz. Wybierz odpowiedź która najbliżej opisuje rzeczywistość — nie idealny stan ani to czym marka chce być.</p>
            </div>
            <div className="progress-wrap">
              <div className="progress-meta">
                <span>Postęp diagnozy</span>
                <span>{answered} / {total}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${(answered / total) * 100}%` }} />
              </div>
            </div>
            {BRAND_QUESTIONS.map((q, qi) => (
              <div className="question" key={q.id}>
                <span className="question-num">Pytanie {qi + 1} z {total}</span>
                <div className="question-text">{q.text}</div>
                <div className="options">
                  {q.options.map((opt, oi) => (
                    <button key={oi} className={`option ${brandAnswers[q.id] === oi ? "selected" : ""}`} onClick={() => handleSelect(q.id, oi)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="open-text-section">
              <span className="open-text-label">Kontekst otwarty — Marka</span>
              <p className="open-text-hint">Opisz markę jednym zdaniem: czym jest, dla kogo i co ją wyróżnia. Ta odpowiedź trafi do raportu jako kontekst do weryfikacji wyniku diagnostycznego.</p>
              <textarea
                className="open-textarea"
                placeholder="np. Jesteśmy studio brandingowe które pracuje z ekspertami chcącymi zbudować markę spójną z ich tożsamością"
                value={brandOpenText}
                onChange={e => setBrandOpenText(e.target.value)}
              />
            </div>
            <div className="btn-row">
              <span className="answered-count">{answered}/{total} odpowiedzi</span>
              <button className="btn btn-primary" disabled={!allAnswered} onClick={handleNext}>
                Przejdź do Modułu B →
              </button>
            </div>
          </div>
        )}

        {/* CLIENT MODULE */}
        {phase === "client" && (
          <div className="module">
            <div className="module-header">
              <div className="module-eyebrow">Moduł B</div>
              <div className="module-title">Archetyp Klienta Idealnego</div>
              <p className="module-desc">Odpowiedz na pytania opisując zachowania, motywacje i bóle klienta idealnego — konkretnej osoby, nie segmentu demograficznego.</p>
            </div>
            <div className="progress-wrap">
              <div className="progress-meta">
                <span>Postęp diagnozy</span>
                <span>{answered} / {total}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${(answered / total) * 100}%` }} />
              </div>
            </div>
            {CLIENT_QUESTIONS.map((q, qi) => (
              <div className="question" key={q.id}>
                <span className="question-num">Pytanie {qi + 1} z {total}</span>
                <div className="question-text">{q.text}</div>
                <div className="options">
                  {q.options.map((opt, oi) => (
                    <button key={oi} className={`option ${clientAnswers[q.id] === oi ? "selected" : ""}`} onClick={() => handleSelect(q.id, oi)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="open-text-section">
              <span className="open-text-label">Kontekst otwarty — Klient Idealny</span>
              <p className="open-text-hint">Opisz klienta idealnego jednym zdaniem: kim jest, czego szuka i co go powstrzymuje. Ta odpowiedź trafi do raportu jako kontekst weryfikacyjny.</p>
              <textarea
                className="open-textarea"
                placeholder="np. „Psycholog w prywatnej praktyce, 35-45 lat, chce być widoczna online ale nie wie jak mówić o sobie bez poczucia że się 'sprzedaje'"
                value={clientOpenText}
                onChange={e => setClientOpenText(e.target.value)}
              />
            </div>
            <div className="btn-row">
              <button className="btn btn-ghost" onClick={handleBack}>← Wróć do Modułu A</button>
              <button className="btn btn-primary" disabled={!allAnswered} onClick={handleNext}>
                Generuj Raport →
              </button>
            </div>
          </div>
        )}

        {/* RESULTS */}
        {phase === "results" && (
          <>
            {!gateSubmitted ? (
              <div className="module">
                <div className="module-header" style={{ textAlign: "center", borderBottom: "none", marginBottom: 0, paddingBottom: 0 }}>
                  <div className="module-eyebrow">Diagnoza zakończona</div>
                  <div className="module-title" style={{ marginBottom: 8 }}>Twój raport jest gotowy</div>
                  <p className="module-desc" style={{ maxWidth: 460, margin: "0 auto 32px" }}>
                    Podaj adres email żeby zobaczyć wyniki diagnozy. Twoje wyniki pojawią się natychmiast.
                  </p>
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "center", marginBottom: 12 }}>
                  <input
                    type="email"
                    placeholder="twoj@email.com"
                    value={gateEmail}
                    onChange={e => { setGateEmail(e.target.value); setGateError(""); }}
                    onKeyDown={e => e.key === "Enter" && handleGateSubmit()}
                    style={{
                      flex: 1, minWidth: 240, maxWidth: 340,
                      background: "var(--surface2)", border: "1px solid var(--border)",
                      borderRadius: 8, padding: "13px 16px", fontSize: 14,
                      color: "var(--text)", fontFamily: "inherit", outline: "none"
                    }}
                  />
                  <button className="btn btn-primary" onClick={handleGateSubmit} disabled={gateSending} style={{ minWidth: 180 }}>
                    {gateSending ? "Chwilę..." : "Pokaż raport →"}
                  </button>
                </div>
                {gateError && <p style={{ color: "#E74C3C", fontSize: 13, marginTop: 8, textAlign: "center" }}>{gateError}</p>}
                <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 14, lineHeight: 1.6, textAlign: "center" }}>
                  Nie wysyłamy spamu. Email służy wyłącznie do kontaktu w sprawie metodologii Selvus™.
                </p>
              </div>
            ) : (
              <>
            <div className="results-header">
              <div className="results-eyebrow">Raport Selvus™</div>
              <h2 className="results-title">Analiza Rezonansu Marki</h2>
              <p className="results-sub">Na podstawie {BRAND_QUESTIONS.length + CLIENT_QUESTIONS.length} odpowiedzi w obu modułach</p>
            </div>

            <div className="result-grid">
              <ArchCard arch={brandTop} label="Archetyp Marki" />
              <ArchCard arch={clientTop} label="Archetyp Klienta Idealnego" />
            </div>

            <div className="secondary-note">
              <strong>Archetypy drugiego planu:</strong> Marka — <strong style={{ color: ARCHETYPES[brandSecond]?.accent }}>{brandSecond}</strong> ({brandScores[brandSecond]} pkt) &nbsp;|&nbsp; Klient — <strong style={{ color: ARCHETYPES[clientSecond]?.accent }}>{clientSecond}</strong> ({clientScores[clientSecond]} pkt).
              <span style={{ color: "var(--muted)", marginLeft: 6 }}>Wysoki wynik drugiego archetypu sugeruje złożoność tożsamości — warto omówić na sesji.</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 24 }}>
              <div className="match-section" style={{ padding: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--muted)", marginBottom: 16 }}>Rozkład — Marka</div>
                <ScoreBars scores={brandScores} topArch={brandTop} />
              </div>
              <div className="match-section" style={{ padding: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--muted)", marginBottom: 16 }}>Rozkład — Klient</div>
                <ScoreBars scores={clientScores} topArch={clientTop} />
              </div>
            </div>

            <div className="match-section" style={{ marginTop: 20 }}>
              <div className="match-title">Siła rezonansu: {brandTop} ↔ {clientTop}</div>
              <div className="match-meter">
                <div className="meter-track">
                  <div className="meter-fill" style={{ width: `${(match.score / 5) * 100}%`, background: `linear-gradient(90deg, ${matchColor}, ${matchColor}90)` }} />
                </div>
                <div className="meter-score" style={{ color: matchColor }}>{match.score}/5</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: matchColor, marginBottom: 12 }}>{matchLabel}</div>
              <div className="match-desc">{match.desc}</div>
            </div>

            {/* SYGNAŁY — tylko PDF stratega */}
            {false && <div className="match-section">
              <div className="match-title">Sygnały projektowe</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: ARCHETYPES[brandTop]?.accent, marginBottom: 12 }}>Marka — {brandTop}</div>
                  <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.7 }}>
                    <span style={{ display: "block", marginBottom: 6 }}>🎨 Kolor dominanta: <span style={{ color: ARCHETYPES[brandTop]?.accent, fontWeight: 600 }}>{ARCHETYPES[brandTop]?.accent}</span></span>
                    <span style={{ display: "block", marginBottom: 6 }}>✦ Pozycjonowanie: „{ARCHETYPES[brandTop]?.tagline}"</span>
                    <span style={{ display: "block" }}>❤ Lojalność przez: {ARCHETYPES[brandTop]?.loyalty}</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: ARCHETYPES[clientTop]?.accent, marginBottom: 12 }}>Klient — {clientTop}</div>
                  <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.7 }}>
                    <span style={{ display: "block", marginBottom: 6 }}>⚡ Główny strach: {ARCHETYPES[clientTop]?.fear}</span>
                    <span style={{ display: "block", marginBottom: 6 }}>✦ Decyzja gdy: ból znika przez {ARCHETYPES[clientTop]?.loyalty?.toLowerCase()}</span>
                    <span style={{ display: "block" }}>❤ Pozostaje lojalny gdy: {ARCHETYPES[clientTop]?.loyalty}</span>
                  </div>
                </div>
              </div>
            </div>}

            {/* REKOMENDACJE — tylko PDF stratega */}

            {/* DISCLAIMER — Poziom 2 */}
            <div className="match-section" style={{ borderColor: "rgba(245,158,11,0.35)", background: "rgba(245,158,11,0.04)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#F59E0B", marginBottom: 14 }}>Ważne — interpretacja wyników</div>
              <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.7, marginBottom: 10 }}>
                Ten raport to punkt wyjścia — nie gotowa strategia komunikacyjna. Selvus™ diagnozuje dominujące wzorce i generuje rekomendacje na podstawie odpowiedzi w kwestionariuszu.
              </p>
              <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.7, marginBottom: 20 }}>
                Pełna interpretacja wyników i przełożenie diagnozy na konkretne decyzje projektowe wymaga <strong style={{ color: "var(--text)" }}>sesji diagnostycznej Selvus™</strong> z Michałem Chojnackim. Dopiero wtedy raport staje się mapą komunikacyjną gotową do wdrożenia.
              </p>
              <a
                href="https://calendly.com/michal-chojnacki-kontakt/30min"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block", background: "#F59E0B", color: "#000",
                  padding: "12px 24px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  textDecoration: "none", fontFamily: "inherit", letterSpacing: 0.3
                }}
              >
                Umów sesję diagnostyczną Selvus™ →
              </a>
            </div>

            {/* Kontekst otwarty — tylko PDF stratega */}



            <div className="restart-row">
              <button className="btn btn-ghost" onClick={handleRestart}>↺ Rozpocznij nową diagnozę</button>
            </div>
            </>
            )}
          </>
        )}

      </div>
    </>
  );
}
